import axios from 'axios'
import {formatApiError, isNetworkError} from '../../../shared/utils/apiErrorMessage'
import {serializeProcurementQueryParams} from './procurementQueryParams'
import {
  downloadBlobFile,
  parseApiErrorMessageFromBlob,
  parseFilenameFromContentDisposition,
} from './procurementDownloadUtils'
import {
  SubcatalogueType,
  Part,
  CreatePartRequest,
  PartStorageLocation,
  InventoryItem,
  InventoryItemFor,
  MasterInventoryItem,
  CreateInventoryRequest,
  UpdateInventoryRequest,
  InventoryPageResponse,
  PageResponse,
  VesselInventoryProjection,
  UnitOfMeasure,
  Requisition,
  CreateRequisitionRequest,
  UpdateRequisitionRequest,
  RequisitionPageResponse,
  RequisitionListQuery,
  NextCodeResponse,
  Port,
  CreatePortRequest,
  ApproveVesselRequest,
  ApproveShoreRequest,
  FileRefDto,
  RFQ,
  RFQListSummary,
  QuotationComparisonResponse,
  CreateRFQFromRequisitionRequest,
  UpdateRFQRequest,
  RFQPageResponse,
  RFQListQuery,
  RfqVendorCandidatesResponse,
  RegionOption,
  Vendor,
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorPageResponse,
  InventoryCategory,
  InventoryItemHead,
  CreateInventoryItemHeadRequest,
  InventoryItemSubHead,
  CreateInventoryItemSubHeadRequest,
  InventoryItemSubCategory,
  AccountingAccount,
  SubAccount,
  InventoryAddress,
  CreateInventoryAddressRequest,
  SubCatalogue,
  SubComponent,
  CreateSubComponentRequest,
  Equipment,
  EquipmentComponent,
  CompanyAdmin,
  CompanyPoInfo,
  CompanyGroupAdmin,
  RequisitionFormCompanyLookup,
  RequisitionFormEquipmentLookup,
  RequisitionFormVesselLookup,
  PurchaseOrderMutationResponse,
  PurchaseOrderNumberPreview,
  RequisitionMutationResponse,
  SubCompanyAdmin,
  Vessel,
  PurchaseOrder,
  RequisitionFormInventoryLookup,
  PurchaseOrderApprovalSummary,
  PurchaseOrderListPageResponse,
  PurchaseOrderPageResponse,
  GoodsReceipt,
  GoodsReceiptDetail,
  GoodsReceiptListRow,
  GoodsReceiptReceivablePoSelectorRow,
  GoodsReceiptReceiptContext,
  GoodsReceiptPageResponse,
  CreateGoodsReceiptRequest,
  Invoice,
  InvoiceDetail,
  InvoiceApprovalSummary,
  InvoiceListRow,
  InvoicePageResponse,
  CreateInvoiceRequest,
  InvoiceEligiblePurchaseOrder,
  InvoiceEligibleGoodsReceipt,
  Delivery,
  DeliveryPageResponse,
  ProcurementAuditLog,
  ProcurementAuditLogDetail,
  ProcurementAuditLogSummary,
  ProcurementAuditLogSummaryPageResponse,
  DepartmentMaster,
  SubDepartmentMaster,
  ProcurementTermsConditionsTemplate,
  ApprovalPolicy,
  ApprovalInstance,
  ApprovalInstanceActionType,
  ApprovalAvailableActions,
  ApprovalEligibility,
  ApprovalLimit,
  CurrencyExchangeRate,
  CurrencyExchangeRateResolveRequest,
  CurrencyExchangeRateUpsertRequest,
  CurrencySnapshotBackfillPreview,
  CurrencySnapshotBackfillRequest,
  CurrencySnapshotBackfillResult,
  CurrencyConversionPreviewRequest,
  CurrencyConversionPreviewResult,
  CurrencyRateSourceType,
  QuotationCurrencyContext,
  DirectPurchaseOrderRequest,
} from './_models'
import {normalizeEquipmentRecords} from './equipmentOptions'

const API_URL = process.env.REACT_APP_API_URL || '/api'
const procurementErr = (error: unknown, fallback: string) =>
  new Error(formatApiError(error, fallback, 'load'))

const SUBCATALOGUE_TYPES_URL = `${API_URL}/subcatalogue-types`
const PARTS_URL = `${API_URL}/parts`
const PART_STORAGE_LOCATIONS_URL = `${API_URL}/part-storage-locations`
const INVENTORY_URL = `${API_URL}/inventory`
const INVENTORY_GENERAL_URL = `${INVENTORY_URL}/general`
const MASTER_INVENTORY_ITEMS_URL = `${INVENTORY_URL}/master-items`
const UOM_URL = `${API_URL}/uoms`
const REQUISITIONS_URL = `${API_URL}/requisitions`
const DEPARTMENTS_URL = `${API_URL}/departments`
const PORTS_URL = `${API_URL}/ports`
const RFQS_URL = `${API_URL}/rfqs`
const QUOTATIONS_URL = `${API_URL}/quotations`
const VENDORS_URL = `${API_URL}/vendors`
const INVENTORY_CATEGORIES_URL = `${API_URL}/inventory-item-categories/inventory-item-categories`
const INVENTORY_CATEGORIES_CREATE_URL = `${API_URL}/inventory-item-categories`
const INVENTORY_ITEM_HEADS_URL = `${API_URL}/inventory/item-heads`
const INVENTORY_ITEM_SUBHEADS_URL = `${API_URL}/inventory/item-subheads`
const INVENTORY_ADDRESSES_URL = `${API_URL}/inventory/addresses`
const INVENTORY_ITEM_SUBCATEGORIES_URL = `${API_URL}/inventory-subcategories`
const ACCOUNTING_URL = `${API_URL}/accounting/accounts`
const SUBACCOUNTS_URL = `${API_URL}/accounting/subaccounts`
const SUBCATALOGUES_URL = `${API_URL}/master-asset/subcatalogues`
const SUBCOMPONENTS_URL = `${API_URL}/subcomponents`
const EQUIPMENT_URL = `${API_URL}/equipment`
const EQUIPMENT_COMPONENTS_URL = `${API_URL}/equipment-components`
const GOODS_RECEIPTS_URL = `${API_URL}/grns`
const INVOICES_URL = `${API_URL}/invoices`
const DELIVERIES_URL = `${API_URL}/deliveries`
const PROCUREMENT_LOGS_URL = `${API_URL}/procurement-logs`
const PROCUREMENT_DASHBOARD_URL = `${API_URL}/procurement/dashboard`
const COMPANY_PO_INFO_URL = `${API_URL}/company-po-info`
const REGIONS_URL = `${API_URL}/regions`
const TERMS_CONDITIONS_LIBRARY_URL = `${API_URL}/procurement/library/terms`
const APPROVAL_POLICIES_URL = `${API_URL}/procurement/approval-policies`
const APPROVAL_INSTANCES_URL = `${API_URL}/procurement/approval-instances`
const CURRENCY_EXCHANGE_RATES_URL = `${API_URL}/procurement/currency-exchange-rates`

const normalizePagedResponse = <T>(
  raw: any,
  page: number = 0,
  size: number = 10
): {
  content: T[]
  pageable: {
    pageNumber: number
    pageSize: number
    sort: {sorted: boolean; empty: boolean; unsorted: boolean}
    offset: number
    paged: boolean
    unpaged: boolean
  }
  last: boolean
  totalPages: number
  totalElements: number
  size: number
  number: number
  sort: {sorted: boolean; empty: boolean; unsorted: boolean}
  first: boolean
  numberOfElements: number
  empty: boolean
} => {
  if (raw && Array.isArray(raw.content)) {
    return raw
  }

  const content: T[] = Array.isArray(raw) ? raw : raw ? [raw] : []
  const totalElements = content.length
  const totalPages = totalElements > 0 ? Math.ceil(totalElements / Math.max(size, 1)) : 1

  return {
    content,
    pageable: {
      pageNumber: page,
      pageSize: size,
      sort: {sorted: false, empty: true, unsorted: true},
      offset: page * size,
      paged: true,
      unpaged: false,
    },
    last: page >= totalPages - 1,
    totalPages,
    totalElements,
    size,
    number: page,
    sort: {sorted: false, empty: true, unsorted: true},
    first: page === 0,
    numberOfElements: content.length,
    empty: content.length === 0,
  }
}

// ——— SUBCATALOGUE TYPES (Categories) ——————————————————————————————————————

export const getSubcatalogueTypes = async (): Promise<SubcatalogueType[]> => {
  try {
    const response = await axios.get<SubcatalogueType[]>(SUBCATALOGUE_TYPES_URL)
    return response.data
  } catch (error: any) {
    console.error('Error fetching subcatalogue types:', error)
    throw new Error(error.response?.data?.message || 'Error fetching subcatalogue types')
  }
}

// ——— PARTS ——————————————————————————————————————————————————————————————————

export const getParts = async (): Promise<Part[]> => {
  try {
    const response = await axios.get<any>(PARTS_URL)
    return response.data.content || response.data
  } catch (error: any) {
    console.error('Error fetching parts:', error)
    throw new Error(error.response?.data?.message || 'Error fetching parts')
  }
}

export const getPartById = async (partId: number): Promise<Part> => {
  try {
    const response = await axios.get<Part>(`${PARTS_URL}/${partId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching part:', error)
    throw new Error(error.response?.data?.message || 'Error fetching part')
  }
}

export const createPart = async (payload: Partial<CreatePartRequest>): Promise<Part> => {
  try {
    const response = await axios.post<Part>(PARTS_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating part:', error)
    throw new Error(error.response?.data?.message || 'Error creating part')
  }
}

export const updatePart = async (
  partId: number,
  payload: Partial<CreatePartRequest>
): Promise<Part> => {
  try {
    const response = await axios.put<Part>(`${PARTS_URL}/${partId}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating part:', error)
    throw new Error(error.response?.data?.message || 'Error updating part')
  }
}

export const deletePart = async (partId: number): Promise<void> => {
  try {
    await axios.delete(`${PARTS_URL}/${partId}`)
  } catch (error: any) {
    console.error('Error deleting part:', error)
    throw new Error(error.response?.data?.message || 'Error deleting part')
  }
}

export const getPartStorageLocations = async (): Promise<PartStorageLocation[]> => {
  try {
    const response = await axios.get<PartStorageLocation[]>(PART_STORAGE_LOCATIONS_URL)
    return response.data
  } catch (error: any) {
    console.error('Error fetching part storage locations:', error)
    throw new Error(error.response?.data?.message || 'Error fetching part storage locations')
  }
}

export const createPartStorageLocation = async (name: string): Promise<PartStorageLocation> => {
  try {
    const response = await axios.post<PartStorageLocation>(PART_STORAGE_LOCATIONS_URL, {name})
    return response.data
  } catch (error: any) {
    console.error('Error creating storage location:', error)
    throw new Error(error.response?.data?.message || 'Error creating storage location')
  }
}

export const updatePartStorageLocation = async (
  locationId: number,
  name: string
): Promise<PartStorageLocation> => {
  try {
    const response = await axios.put<PartStorageLocation>(
      `${PART_STORAGE_LOCATIONS_URL}/${locationId}`,
      {name}
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating storage location:', error)
    throw new Error(error.response?.data?.message || 'Error updating storage location')
  }
}

export const deletePartStorageLocation = async (locationId: number): Promise<void> => {
  try {
    await axios.delete(`${PART_STORAGE_LOCATIONS_URL}/${locationId}`)
  } catch (error: any) {
    console.error('Error deleting storage location:', error)
    throw new Error(error.response?.data?.message || 'Error deleting storage location')
  }
}

export const createPartStorageSubLocation = async (
  locationId: number,
  name: string,
  optional?: boolean
): Promise<void> => {
  try {
    await axios.post(`${PART_STORAGE_LOCATIONS_URL}/${locationId}/sub-locations`, {name, optional})
  } catch (error: any) {
    console.error('Error creating sub storage location:', error)
    throw new Error(error.response?.data?.message || 'Error creating sub storage location')
  }
}

export const updatePartStorageSubLocation = async (
  subLocationId: number,
  name: string,
  optional?: boolean
): Promise<void> => {
  try {
    await axios.put(`${PART_STORAGE_LOCATIONS_URL}/sub-locations/${subLocationId}`, {
      name,
      optional,
    })
  } catch (error: any) {
    console.error('Error updating sub storage location:', error)
    throw new Error(error.response?.data?.message || 'Error updating sub storage location')
  }
}

export const deletePartStorageSubLocation = async (subLocationId: number): Promise<void> => {
  try {
    await axios.delete(`${PART_STORAGE_LOCATIONS_URL}/sub-locations/${subLocationId}`)
  } catch (error: any) {
    console.error('Error deleting sub storage location:', error)
    throw new Error(error.response?.data?.message || 'Error deleting sub storage location')
  }
}

export const createPartStorageSegment = async (
  subLocationId: number,
  name: string,
  optional?: boolean
): Promise<void> => {
  try {
    await axios.post(`${PART_STORAGE_LOCATIONS_URL}/sub-locations/${subLocationId}/segments`, {
      name,
      optional,
    })
  } catch (error: any) {
    console.error('Error creating storage segment:', error)
    throw new Error(error.response?.data?.message || 'Error creating storage segment')
  }
}

export const updatePartStorageSegment = async (
  segmentId: number,
  name: string,
  optional?: boolean
): Promise<void> => {
  try {
    await axios.put(`${PART_STORAGE_LOCATIONS_URL}/segments/${segmentId}`, {name, optional})
  } catch (error: any) {
    console.error('Error updating storage segment:', error)
    throw new Error(error.response?.data?.message || 'Error updating storage segment')
  }
}

export const deletePartStorageSegment = async (segmentId: number): Promise<void> => {
  try {
    await axios.delete(`${PART_STORAGE_LOCATIONS_URL}/segments/${segmentId}`)
  } catch (error: any) {
    console.error('Error deleting storage segment:', error)
    throw new Error(error.response?.data?.message || 'Error deleting storage segment')
  }
}

// ——— INVENTORY ——————————————————————————————————————————————————————————————

export const getInventory = async (
  vesselId: number | null,
  page: number = 0,
  size: number = 20,
  cgaId?: number
): Promise<InventoryPageResponse> => {
  try {
    const params: any = {
      page,
      size,
    }
    // Only add vesselId if it's provided (for non-superadmin users)
    if (vesselId !== null && vesselId !== undefined) {
      params.vesselId = vesselId
    }
    if (cgaId !== undefined) {
      params.cgaId = cgaId
    }
    const response = await axios.get(INVENTORY_URL, {
      params,
    })
    console.log('secondinventory raw API response:', response.data)

    let pageResponse: InventoryPageResponse

    if (Array.isArray(response.data)) {
      // Backend returned raw array → wrap it into a valid Page object
      const content = response.data as InventoryItem[]
      pageResponse = {
        content,
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: {sorted: false, unsorted: true, empty: false},
          offset: page * size,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: content.length > 0 ? Math.ceil(content.length / size) : 1,
        totalElements: content.length,
        size,
        number: page,
        sort: {sorted: false, unsorted: true, empty: false},
        first: page === 0,
        numberOfElements: content.length,
        empty: content.length === 0,
      }
    } else {
      // Proper Page object (or fallback)
      pageResponse = response.data as InventoryPageResponse
      if (!Array.isArray(pageResponse.content)) {
        pageResponse.content = []
      }
    }

    console.log('secondinventory normalized Page object:', pageResponse)
    return pageResponse
  } catch (error: any) {
    console.error('Error fetching inventory:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory')
  }
}

export const getRequisitionFormInventory = async (params?: {
  vesselId?: number
  q?: string
  limit?: number
}): Promise<RequisitionFormInventoryLookup[]> => {
  try {
    const response = await axios.get<RequisitionFormInventoryLookup[]>(
      `${INVENTORY_URL}/requisition-form`,
      {params}
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching requisition form inventory:', error)
    throw new Error(error.response?.data?.message || 'Error fetching requisition form inventory')
  }
}

export const getInventoryById = async (inventoryId: number): Promise<InventoryItem> => {
  try {
    const response = await axios.get<InventoryItem>(`${INVENTORY_URL}/${inventoryId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching inventory item:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory item')
  }
}

export const getGeneralInventoryItems = async (
  vesselId?: number | null,
  q?: string,
  inventoryItemSubCategoryId?: number | null
): Promise<InventoryItem[]> => {
  try {
    const params: any = {}
    if (vesselId !== null && vesselId !== undefined) {
      params.vesselId = vesselId
    }
    if (inventoryItemSubCategoryId !== null && inventoryItemSubCategoryId !== undefined) {
      params.inventoryItemSubCategoryId = inventoryItemSubCategoryId
    }
    if (q) {
      params.q = q
    }
    const response = await axios.get<InventoryItem[]>(INVENTORY_GENERAL_URL, {params})
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching general inventory items:', error)
    throw new Error(error.response?.data?.message || 'Error fetching general inventory items')
  }
}

export const createInventory = async (payload: CreateInventoryRequest): Promise<InventoryItem> => {
  try {
    const response = await axios.post<InventoryItem>(INVENTORY_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating inventory item:', error)
    throw new Error(error.response?.data?.message || 'Error creating inventory item')
  }
}

export const createGeneralInventoryItem = async (
  payload: CreateInventoryRequest
): Promise<InventoryItem> => {
  try {
    const response = await axios.post<InventoryItem>(INVENTORY_GENERAL_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating general inventory item:', error)
    throw new Error(error.response?.data?.message || 'Error creating general inventory item')
  }
}

export const getMasterInventoryItems = async (params?: {
  itemFor?: InventoryItemFor | null
  active?: boolean | null
  search?: string
  page?: number
  size?: number
}): Promise<PageResponse<MasterInventoryItem>> => {
  try {
    const response = await axios.get<PageResponse<MasterInventoryItem>>(
      MASTER_INVENTORY_ITEMS_URL,
      {
        params,
      }
    )
    return normalizePagedResponse<MasterInventoryItem>(
      response.data,
      params?.page ?? 0,
      params?.size ?? 25
    )
  } catch (error: any) {
    console.error('Error fetching master inventory items:', error)
    throw new Error(error.response?.data?.message || 'Error fetching master inventory items')
  }
}

export const getVesselInventoryProjection = async (params: {
  vesselId: number
  itemFor?: InventoryItemFor | null
  search?: string
  inventoryItemSubCategoryId?: number | null
  inventoryItemSubCategoryId?: number | null
  page?: number
  size?: number
}): Promise<PageResponse<VesselInventoryProjection>> => {
  try {
    const response = await axios.get<PageResponse<VesselInventoryProjection>>(
      `${MASTER_INVENTORY_ITEMS_URL}/projection`,
      {params}
    )
    return normalizePagedResponse<VesselInventoryProjection>(
      response.data,
      params.page ?? 0,
      params.size ?? 25
    )
  } catch (error: any) {
    console.error('Error fetching vessel inventory projection:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel inventory projection')
  }
}

export const getOrCreateMasterInventoryInstance = async (
  vesselId: number,
  masterItemId: number
): Promise<InventoryItem> => {
  try {
    const response = await axios.post<InventoryItem>(
      `${MASTER_INVENTORY_ITEMS_URL}/instances`,
      null,
      {
        params: {vesselId, masterItemId},
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating vessel inventory instance:', error)
    throw new Error(error.response?.data?.message || 'Error creating vessel inventory instance')
  }
}

export const updateInventory = async (
  inventoryId: number,
  payload: UpdateInventoryRequest
): Promise<InventoryItem> => {
  try {
    const response = await axios.put<InventoryItem>(`${INVENTORY_URL}/${inventoryId}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating inventory item:', error)
    throw new Error(error.response?.data?.message || 'Error updating inventory item')
  }
}

export const deleteInventory = async (inventoryId: number): Promise<void> => {
  try {
    await axios.delete(`${INVENTORY_URL}/${inventoryId}`)
  } catch (error: any) {
    console.error('Error deleting inventory item:', error)
    throw new Error(error.response?.data?.message || 'Error deleting inventory item')
  }
}

// ——— UNIT OF MEASURE ————————————————————————————————————————————————————————

export const getUnitsOfMeasure = async (includeInactive: boolean = false): Promise<UnitOfMeasure[]> => {
  try {
    const response = await axios.get<UnitOfMeasure[]>(UOM_URL, {params: {includeInactive}})
    return response.data
  } catch (error: any) {
    console.error('Error fetching units of measure:', error)
    throw new Error(error.response?.data?.message || 'Error fetching units of measure')
  }
}

export const createUnitOfMeasure = async (
  payload: Omit<UnitOfMeasure, 'id'>
): Promise<UnitOfMeasure> => {
  try {
    const response = await axios.post<UnitOfMeasure>(UOM_URL, payload)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Error creating unit of measure')
  }
}

export const updateUnitOfMeasure = async (
  id: number,
  payload: Omit<UnitOfMeasure, 'id'>
): Promise<UnitOfMeasure> => {
  try {
    const response = await axios.put<UnitOfMeasure>(`${UOM_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Error updating unit of measure')
  }
}

export const deleteUnitOfMeasure = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${UOM_URL}/${id}`)
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Error deleting unit of measure')
  }
}

// ——— REQUISITIONS ———————————————————————————————————————————————————————————

// Test API connectivity
export const testRequisitionAPI = async (): Promise<boolean> => {
  try {
    console.log('Testing API connectivity to:', REQUISITIONS_URL)
    console.log('Full API URL:', API_URL)
    console.log('Environment REACT_APP_API_URL:', process.env.REACT_APP_API_URL)

    // First test with a simple GET request
    const response = await axios.get(REQUISITIONS_URL, {
      timeout: 5000,
      headers: {
        Accept: 'application/json',
      },
    })
    console.log('GET API test successful:', response.status, response.data)

    // Test if POST is supported by sending an OPTIONS request
    try {
      const optionsResponse = await axios.options(REQUISITIONS_URL)
      console.log('OPTIONS response:', optionsResponse.headers)
    } catch (optionsError) {
      console.log('OPTIONS request failed (this is normal for some APIs)')
    }

    return true
  } catch (error: any) {
    console.error('API test failed:', error.message)
    console.error('Error status:', error.response?.status)
    console.error('Error details:', error.response?.data)
    console.error('Error headers:', error.response?.headers)
    return false
  }
}

// Test function to verify API integration with sample data
export const testCreateRequisitionWithFormData = async (): Promise<void> => {
  try {
    console.log('Testing requisition creation with FormData...')

    const testPayload: CreateRequisitionRequest = {
      title: 'Test Starter spares for main engine',
      requisition_code: 'REQ-TEST-001',
      description: 'Test requisition for API integration',
      type: 'MEDIUM',
      categoryId: 2,
      vesselId: 2,
      requestedPortId: 5,
      requiredDeliveryDate: '2025-11-20',
      partId: 45,
      isCompletePartRequired: false,
      quantity: 10,
      uom: 'PCS',
      estimatedUnitPrice: 1200.5,
      equipmentLink: null,
      status: 'DRAFT',
    }

    const result = await createRequisition(testPayload)
    console.log('Test requisition created successfully:', result)

    // Clean up - try to delete the test requisition
    try {
      await deleteRequisition(result.id)
      console.log('Test requisition cleaned up successfully')
    } catch (cleanupError) {
      console.warn('Could not clean up test requisition:', cleanupError)
    }
  } catch (error: any) {
    console.error('Test failed:', error)
    throw error
  }
}

export const getNextRequisitionCode = async (vesselId?: number | null): Promise<string> => {
  try {
    const params: any = {}
    if (vesselId !== null && vesselId !== undefined) {
      params.vesselId = vesselId
    }
    const response = await axios.get<NextCodeResponse>(`${REQUISITIONS_URL}/next-code`, {params})
    return response.data.nextCode
  } catch (error: any) {
    console.error('Error fetching next requisition code:', error)
    // Return a fallback code if the API doesn't support this endpoint
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
    const randomNum = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0')
    return `REQ-${timestamp}-${randomNum}`
  }
}

export const getRequisitions = async (
  page?: number,
  size?: number,
  vesselIdOrQuery?: number | null | RequisitionListQuery,
  q?: string
): Promise<RequisitionPageResponse> => {
  try {
    const params: Record<string, unknown> = {}
    const query: RequisitionListQuery =
      vesselIdOrQuery !== null &&
      vesselIdOrQuery !== undefined &&
      typeof vesselIdOrQuery === 'object'
        ? (vesselIdOrQuery as RequisitionListQuery)
        : {
            vesselId:
              vesselIdOrQuery !== null && vesselIdOrQuery !== undefined
                ? (vesselIdOrQuery as number)
                : undefined,
            search: q,
          }
    // Only add pagination params if they are provided
    if (page !== undefined && page !== null) {
      params.page = page
    }
    if (size !== undefined && size !== null) {
      params.size = size
    }
    // Only add vesselId if it's provided and not null (for non-superadmin users)
    if (query.vesselId !== null && query.vesselId !== undefined) {
      params.vesselId = query.vesselId
    }
    if (query.search && query.search.trim() !== '') {
      params.search = query.search.trim()
    }
    if (query.status) {
      params.status = query.status
    }
    if (query.reference) {
      params.reference = query.reference.trim()
    }
    if (query.title) {
      params.title = query.title.trim()
    }
    if (query.category) {
      params.category = query.category.trim()
    }
    if (query.head) {
      params.head = query.head.trim()
    }
    if (query.subHead) {
      params.subHead = query.subHead.trim()
    }
    if (query.dateFrom) {
      params.dateFrom = query.dateFrom
    }
    if (query.dateTo) {
      params.dateTo = query.dateTo
    }
    if (query.tab) {
      params.tab = query.tab
    }
    if (query.approvalPendingWith) {
      params.approvalPendingWith = query.approvalPendingWith
    }
    if (query.deliveredToVessel) {
      params.deliveredToVessel = query.deliveredToVessel
    }
    if (Array.isArray(query.sort) && query.sort.length > 0) {
      params.sort = query.sort
    }

    const response = await axios.get<any>(REQUISITIONS_URL, {
      params,
      paramsSerializer: serializeProcurementQueryParams,
      headers: {
        Accept: 'application/json',
      },
    })

    // Use defaults for pagination metadata when not provided
    const pageNum = page ?? 0
    const pageSize =
      size ??
      (Array.isArray(response.data) ? response.data.length : response.data?.content?.length ?? 0)

    // Handle both paginated and direct array responses
    if (Array.isArray(response.data)) {
      // Direct array response - convert to paginated format
      return {
        content: response.data,
        pageable: {
          pageNumber: pageNum,
          pageSize: pageSize,
          sort: {sorted: false, empty: true, unsorted: true},
          offset: pageNum * pageSize,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: 1,
        totalElements: response.data.length,
        size: response.data.length,
        number: pageNum,
        sort: {sorted: false, empty: true, unsorted: true},
        first: true,
        numberOfElements: response.data.length,
        empty: response.data.length === 0,
      }
    } else if (response.data && response.data.content) {
      // Paginated response
      return response.data
    } else {
      // Single object response - wrap in array
      const singleItem = response.data
      return {
        content: singleItem ? [singleItem] : [],
        pageable: {
          pageNumber: pageNum,
          pageSize: pageSize,
          sort: {sorted: false, empty: true, unsorted: true},
          offset: pageNum * pageSize,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: 1,
        totalElements: singleItem ? 1 : 0,
        size: singleItem ? 1 : 0,
        number: pageNum,
        sort: {sorted: false, empty: true, unsorted: true},
        first: true,
        numberOfElements: singleItem ? 1 : 0,
        empty: !singleItem,
      }
    }
  } catch (error: unknown) {
    if (isNetworkError(error)) {
      throw new Error('Unable to reach the server. Check your connection and try again.')
    }
    throw procurementErr(error, 'Error fetching requisitions')
  }
}

export interface RequisitionExportFilters {
  format: 'EXCEL' | 'PDF'
  companyGroupAdminId?: number | null
  companyAdminId?: number | null
  vesselId?: number | null
  requisitionId?: number | null
  requisitionCode?: string | null
  status?: string | null
  categoryId?: number | null
  subcategoryId?: number | null
  dateFrom?: string | null
  dateTo?: string | null
  search?: string | null
  selectedColumns?: string[] | null
}

const normalizeOptionalExportNumber = (value: unknown): number | null | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const normalizeOptionalExportString = (value: unknown): string | null | undefined => {
  if (typeof value !== 'string') {
    return value == null ? undefined : String(value)
  }
  const trimmed = value.trim()
  return trimmed !== '' ? trimmed : undefined
}

const sanitizeRequisitionExportPayload = (
  payload: Omit<RequisitionExportFilters, 'format'> & {format?: 'EXCEL' | 'PDF'}
) => ({
  ...payload,
  format: payload.format,
  companyGroupAdminId: normalizeOptionalExportNumber(payload.companyGroupAdminId),
  companyAdminId: normalizeOptionalExportNumber(payload.companyAdminId),
  vesselId: normalizeOptionalExportNumber(payload.vesselId),
  requisitionId: normalizeOptionalExportNumber(payload.requisitionId),
  categoryId: normalizeOptionalExportNumber(payload.categoryId),
  subcategoryId: normalizeOptionalExportNumber(payload.subcategoryId),
  requisitionCode: normalizeOptionalExportString(payload.requisitionCode),
  status: normalizeOptionalExportString(payload.status),
  dateFrom: normalizeOptionalExportString(payload.dateFrom),
  dateTo: normalizeOptionalExportString(payload.dateTo),
  search: normalizeOptionalExportString(payload.search),
  selectedColumns:
    Array.isArray(payload.selectedColumns) && payload.selectedColumns.length > 0
      ? payload.selectedColumns.map((value) => String(value).trim()).filter((value) => value !== '')
      : undefined,
})

export const getRequisitionExportCount = async (
  payload: Omit<RequisitionExportFilters, 'format'> & {format?: 'EXCEL' | 'PDF'}
): Promise<number> => {
  try {
    const response = await axios.post<{count: number}>(
      `${REQUISITIONS_URL}/export/count`,
      sanitizeRequisitionExportPayload(payload),
      {
        headers: {Accept: 'application/json'},
      }
    )
    return Number(response.data?.count ?? 0)
  } catch (error: any) {
    throw procurementErr(error, 'Failed to preview requisition export count')
  }
}

export const exportRequisitions = async (payload: RequisitionExportFilters): Promise<void> => {
  try {
    const sanitizedPayload = sanitizeRequisitionExportPayload(payload) as RequisitionExportFilters
    const response = await axios.post(`${REQUISITIONS_URL}/export`, sanitizedPayload, {
      responseType: 'blob',
      headers: {
        Accept:
          sanitizedPayload.format === 'PDF'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    })
    const fallbackExtension = sanitizedPayload.format === 'PDF' ? 'pdf' : 'xlsx'
    const fallbackName = `requisitions_export.${fallbackExtension}`
    const filename =
      parseFilenameFromContentDisposition(response.headers?.['content-disposition']) || fallbackName
    downloadBlobFile(response.data as Blob, filename)
  } catch (error: any) {
    const data = error?.response?.data
    if (data instanceof Blob) {
      const message = await parseApiErrorMessageFromBlob(data, 'Failed to export requisitions')
      throw new Error(message)
    }
    throw procurementErr(error, 'Failed to export requisitions')
  }
}

export const getRequisitionById = async (requisitionId: number): Promise<Requisition> => {
  try {
    const GET_URL = `${REQUISITIONS_URL}/${requisitionId}`
    console.log('Fetching requisition by ID:', requisitionId, 'from URL:', GET_URL)

    const response = await axios.get<Requisition>(GET_URL, {
      headers: {
        Accept: 'application/json',
      },
    })

    console.log('Requisition fetched successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error fetching requisition:', error)
    throw new Error(error.response?.data?.message || 'Error fetching requisition')
  }
}

export const createRequisition = async (
  payload: CreateRequisitionRequest,
  files?: File[]
): Promise<RequisitionMutationResponse> => {
  try {
    const CREATE_REQUISITION_URL = `${API_URL}/requisitions/create`

    console.log('Creating requisition with payload:', JSON.stringify(payload, null, 2))
    console.log('Sending to URL:', CREATE_REQUISITION_URL)
    console.log('Files to upload:', files?.length || 0)

    // Create FormData
    const formData = new FormData()

    // Add required fields to FormData
    formData.append('title', payload.title)
    if (payload.name) {
      formData.append('name', payload.name)
    }
    formData.append('requisition_code', payload.requisition_code)
    formData.append('description', payload.description)
    if (payload.regionId != null) formData.append('regionId', String(payload.regionId))
    formData.append('type', payload.type)
    if (payload.requestFor) {
      formData.append('requestFor', payload.requestFor)
    }
    formData.append('categoryId', payload.categoryId.toString())
    if (payload.inventoryItemSubCategoryId != null) {
      formData.append('inventoryItemSubCategoryId', String(payload.inventoryItemSubCategoryId))
    }
    formData.append('vesselId', payload.vesselId.toString())
    if (payload.requestedPortId) {
      formData.append('requestedPortId', payload.requestedPortId.toString())
    }
    if (payload.customPortName) {
      formData.append('customPortName', payload.customPortName)
    }
    formData.append('requiredDeliveryDate', payload.requiredDeliveryDate)
    if (payload.linkedDefectId !== null && payload.linkedDefectId !== undefined) {
      formData.append('linkedDefectId', payload.linkedDefectId.toString())
    }
    if (payload.linkedDefectNumber) {
      formData.append('linkedDefectNumber', payload.linkedDefectNumber)
    }
    if (payload.linkedDefectTitle) {
      formData.append('linkedDefectTitle', payload.linkedDefectTitle)
    }
    // Only send equipmentId when it's a real positive id (backend fails on 0/null)
    if (
      payload.equipmentId !== null &&
      payload.equipmentId !== undefined &&
      payload.equipmentId > 0
    ) {
      formData.append('equipmentId', payload.equipmentId.toString())
    }

    // Add optional inventory item head and sub head
    if (payload.inventoryItemHeadId) {
      formData.append('inventoryItemHeadId', payload.inventoryItemHeadId.toString())
    }
    if (payload.inventoryItemSubHeadId) {
      formData.append('inventoryItemSubHeadId', payload.inventoryItemSubHeadId.toString())
    }
    if (payload.accountCodeId) {
      formData.append('accountCodeId', payload.accountCodeId.toString())
    }
    if (payload.subaccountCodeId) {
      formData.append('subaccountCodeId', payload.subaccountCodeId.toString())
    }

    // Add optional item detail fields
    if (payload.make) {
      formData.append('make', payload.make)
    }
    if (payload.model) {
      formData.append('model', payload.model)
    }
    if (payload.serialNumber) {
      formData.append('serialNumber', payload.serialNumber)
    }

    // Add optional part and subcomponent fields
    if (payload.partId) {
      formData.append('partId', payload.partId.toString())
    }
    if (payload.subcomponentId) {
      formData.append('subcomponentId', payload.subcomponentId.toString())
    }

    // Optional manual item (non-technical) requisition
    if (
      payload.manualItemName !== null &&
      payload.manualItemName !== undefined &&
      payload.manualItemName.trim() !== ''
    ) {
      formData.append('manualItemName', payload.manualItemName)
    }

    // Add optional quantity and pricing fields
    if (payload.isCompletePartRequired !== undefined) {
      formData.append('isCompletePartRequired', payload.isCompletePartRequired.toString())
    }
    if (payload.quantity) {
      formData.append('quantity', payload.quantity.toString())
    }
    if (payload.uom) {
      formData.append('uom', payload.uom)
    }
    if (payload.estimatedUnitPrice) {
      formData.append('estimatedUnitPrice', payload.estimatedUnitPrice.toString())
    }
    if (payload.equipmentLink) {
      formData.append('equipmentLink', payload.equipmentLink)
    }

    if (payload.lines && payload.lines.length > 0) {
      payload.lines.forEach((line, index) => {
        if (line.id !== null && line.id !== undefined) {
          formData.append(`lines[${index}].id`, String(line.id))
        }
        if (line.partId !== null && line.partId !== undefined) {
          formData.append(`lines[${index}].partId`, String(line.partId))
        }
        if (line.inventoryItemId !== null && line.inventoryItemId !== undefined) {
          formData.append(`lines[${index}].inventoryItemId`, String(line.inventoryItemId))
        }
        if (line.masterItemId !== null && line.masterItemId !== undefined) {
          formData.append(`lines[${index}].masterItemId`, String(line.masterItemId))
        }
        if (line.subcomponentId !== null && line.subcomponentId !== undefined) {
          formData.append(`lines[${index}].subcomponentId`, String(line.subcomponentId))
        }
        if (line.subcomponentClubbing) {
          formData.append(`lines[${index}].subcomponentClubbing`, line.subcomponentClubbing)
        }
        if (line.manualItemName) {
          formData.append(`lines[${index}].manualItemName`, line.manualItemName)
        }
        if (line.unitOfMeasurement) {
          formData.append(`lines[${index}].unitOfMeasurement`, line.unitOfMeasurement)
        }
        formData.append(`lines[${index}].quantity`, String(line.quantity))
        if (line.estimatedUnitPrice !== null && line.estimatedUnitPrice !== undefined) {
          formData.append(`lines[${index}].estimatedUnitPrice`, String(line.estimatedUnitPrice))
        }
        if (line.notes) {
          formData.append(`lines[${index}].notes`, line.notes)
        }
      })
    }

    // Add files if provided
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file)
      })
      console.log('Added', files.length, 'files to FormData')
    }

    console.log('FormData entries:')
    formData.forEach((value, key) => {
      console.log(`${key}: ${value}`)
    })

    const response = await axios.post<RequisitionMutationResponse>(CREATE_REQUISITION_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
      timeout: 10000,
    })

    console.log('Requisition created successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error creating requisition:', error)
    console.error('Error message:', error.message)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)
    console.error('Request URL:', error.config?.url)

    if (error.code === 'ECONNREFUSED') {
      throw new Error('Cannot connect to API server. Please check if the server is running.')
    } else if (error.response?.status === 405) {
      throw new Error('Method not allowed. The API endpoint may not support POST requests.')
    } else if (error.response?.status === 404) {
      throw new Error('API endpoint not found. Please check the URL.')
    } else if (error.response?.status === 400) {
      throw new Error('Error creating requisition')
    } else if (error.response?.status === 500) {
      throw new Error('Error creating requisition')
    } else {
      throw new Error('Error creating requisition')
    }
  }
}

export const updateRequisition = async (
  requisitionId: number,
  payload: UpdateRequisitionRequest,
  files?: File[],
  lineAttachmentFilesByIndex?: Record<number, File[]>
): Promise<RequisitionMutationResponse> => {
  try {
    // Use the correct update endpoint format
    const UPDATE_URL = `${API_URL}/requisitions/update/${requisitionId}`

    console.log('=== UPDATE REQUISITION DEBUG ===')
    console.log('Updating requisition with payload:', JSON.stringify(payload, null, 2))
    console.log('Update URL:', UPDATE_URL)
    console.log('Full API_URL:', API_URL)
    console.log('Payload inventoryItemHeadId:', payload.inventoryItemHeadId)
    console.log('Payload inventoryItemSubHeadId:', payload.inventoryItemSubHeadId)
    console.log('Payload make:', payload.make)
    console.log('Payload model:', payload.model)
    console.log('Payload serialNumber:', payload.serialNumber)

    // Create FormData for the update request
    const formData = new FormData()

    // Add required fields
    formData.append('title', payload.title)
    if (payload.name) formData.append('name', payload.name)

    // Add optional fields only if they exist
    if (payload.requisition_code) formData.append('requisition_code', payload.requisition_code)
    if (payload.description) formData.append('description', payload.description)
    if (payload.regionId != null) formData.append('regionId', String(payload.regionId))
    if (payload.type) formData.append('type', payload.type)
    if (payload.requestFor) formData.append('requestFor', payload.requestFor)
    if (payload.inventoryItemSubCategoryId != null) {
      formData.append('inventoryItemSubCategoryId', String(payload.inventoryItemSubCategoryId))
    }
    if (payload.categoryId !== null && payload.categoryId !== undefined) {
      formData.append('categoryId', payload.categoryId.toString())
    }

    // Add inventory item head and sub head IDs
    if (payload.inventoryItemHeadId !== null && payload.inventoryItemHeadId !== undefined) {
      formData.append('inventoryItemHeadId', payload.inventoryItemHeadId.toString())
      console.log('Adding inventoryItemHeadId to FormData:', payload.inventoryItemHeadId)
    }
    if (payload.inventoryItemSubHeadId !== null && payload.inventoryItemSubHeadId !== undefined) {
      formData.append('inventoryItemSubHeadId', payload.inventoryItemSubHeadId.toString())
      console.log('Adding inventoryItemSubHeadId to FormData:', payload.inventoryItemSubHeadId)
    }

    if (payload.accountCodeId !== null && payload.accountCodeId !== undefined) {
      formData.append('accountCodeId', payload.accountCodeId.toString())
    }
    if (payload.subaccountCodeId !== null && payload.subaccountCodeId !== undefined) {
      formData.append('subaccountCodeId', payload.subaccountCodeId.toString())
    }
    if (payload.make) formData.append('make', payload.make)
    if (payload.model) formData.append('model', payload.model)
    if (payload.serialNumber) formData.append('serialNumber', payload.serialNumber)

    if (payload.vesselId) formData.append('vesselId', payload.vesselId.toString())
    if (payload.requestedPortId)
      formData.append('requestedPortId', payload.requestedPortId.toString())
    if (payload.requiredDeliveryDate)
      formData.append('requiredDeliveryDate', payload.requiredDeliveryDate)
    if (payload.linkedDefectId !== null && payload.linkedDefectId !== undefined) {
      formData.append('linkedDefectId', payload.linkedDefectId.toString())
    }
    if (payload.linkedDefectNumber) {
      formData.append('linkedDefectNumber', payload.linkedDefectNumber)
    }
    if (payload.linkedDefectTitle) {
      formData.append('linkedDefectTitle', payload.linkedDefectTitle)
    }

    // API requires exactly one of: equipment/subcomponent/part/manualItemName
    // Only send the fields that are not null
    // Only send equipmentId when it's a valid positive id; backend rejects 0
    if (
      payload.equipmentId !== null &&
      payload.equipmentId !== undefined &&
      payload.equipmentId > 0
    ) {
      formData.append('equipmentId', payload.equipmentId.toString())
    }
    if (payload.subcomponentId !== null && payload.subcomponentId !== undefined) {
      formData.append('subcomponentId', payload.subcomponentId.toString())
    }
    if (payload.partId !== null && payload.partId !== undefined) {
      formData.append('partId', payload.partId.toString())
    }

    if (payload.isCompletePartRequired !== undefined) {
      formData.append('isCompletePartRequired', payload.isCompletePartRequired.toString())
    }

    // Always send manualItemName if it exists (this is what we're updating)
    if (
      payload.manualItemName !== null &&
      payload.manualItemName !== undefined &&
      payload.manualItemName.trim() !== ''
    ) {
      formData.append('manualItemName', payload.manualItemName)
    }

    if (payload.quantity !== undefined) formData.append('quantity', payload.quantity.toString())
    if (payload.uom) formData.append('uom', payload.uom)
    if (payload.estimatedUnitPrice !== undefined)
      formData.append('estimatedUnitPrice', payload.estimatedUnitPrice.toString())

    if (payload.equipmentLink) formData.append('equipmentLink', payload.equipmentLink)

    if (payload.status) formData.append('status', payload.status)

    // Add lines array if present
    if (payload.lines && payload.lines.length > 0) {
      payload.lines.forEach((line, index) => {
        if (line.id !== null && line.id !== undefined) {
          formData.append(`lines[${index}].id`, String(line.id))
        }
        if (line.partId !== null && line.partId !== undefined) {
          formData.append(`lines[${index}].partId`, line.partId.toString())
        }
        if (line.inventoryItemId !== null && line.inventoryItemId !== undefined) {
          formData.append(`lines[${index}].inventoryItemId`, line.inventoryItemId.toString())
        }
        if (line.masterItemId !== null && line.masterItemId !== undefined) {
          formData.append(`lines[${index}].masterItemId`, line.masterItemId.toString())
        }
        if (line.manualItemName) {
          formData.append(`lines[${index}].manualItemName`, line.manualItemName)
        }
        if (line.subcomponentId !== null && line.subcomponentId !== undefined) {
          formData.append(`lines[${index}].subcomponentId`, line.subcomponentId.toString())
        }
        if (line.subcomponentClubbing) {
          formData.append(`lines[${index}].subcomponentClubbing`, line.subcomponentClubbing)
        }
        formData.append(`lines[${index}].quantity`, line.quantity.toString())
        if (line.estimatedUnitPrice !== null && line.estimatedUnitPrice !== undefined) {
          formData.append(`lines[${index}].estimatedUnitPrice`, line.estimatedUnitPrice.toString())
        }
        if (line.unitOfMeasurement) {
          formData.append(`lines[${index}].unitOfMeasurement`, line.unitOfMeasurement)
        }
        if (line.notes) {
          formData.append(`lines[${index}].notes`, line.notes)
        }
        if (line.attachments && line.attachments.length > 0) {
          line.attachments.forEach((attachment, attachmentIndex) => {
            if (attachment?.fileName && attachment?.url) {
              formData.append(
                `lines[${index}].attachments[${attachmentIndex}].fileName`,
                attachment.fileName
              )
              formData.append(`lines[${index}].attachments[${attachmentIndex}].url`, attachment.url)
              if (attachment.sizeBytes !== undefined && attachment.sizeBytes !== null) {
                formData.append(
                  `lines[${index}].attachments[${attachmentIndex}].sizeBytes`,
                  attachment.sizeBytes.toString()
                )
              }
            }
          })
        }
        const lineFiles = lineAttachmentFilesByIndex?.[index]
        if (lineFiles && lineFiles.length > 0) {
          lineFiles.forEach((lineFile) => {
            formData.append(`lines[${index}].lineFiles`, lineFile)
          })
        }
      })
    }

    // Add files if provided
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file)
      })
      console.log('Added', files.length, 'files to update FormData')
    }

    console.log('FormData entries for update:')
    const entries = Array.from(formData.entries())
    entries.forEach(([key, value]) => {
      console.log(`${key}: ${value}`)
    })

    // Debug: Check which item-related fields are being sent
    const itemFields = ['equipmentId', 'subcomponentId', 'partId', 'manualItemName']
    const sentItemFields = entries.filter(([key]) => itemFields.includes(key))
    console.log('Item-related fields being sent:', sentItemFields)
    console.log('Number of item fields:', sentItemFields.length)

    // Debug: Check if HEAD, SUB HEAD, MAKE, MODEL, SERIAL NO are in FormData
    const preservedFields = [
      'inventoryItemHeadId',
      'inventoryItemSubHeadId',
      'make',
      'model',
      'serialNumber',
    ]
    const sentPreservedFields = entries.filter(([key]) => preservedFields.includes(key))
    console.log('🔍 Preserved fields being sent:', sentPreservedFields)
    console.log('🔍 Number of preserved fields:', sentPreservedFields.length)
    console.log('=== END UPDATE DEBUG ===')

    // Create a fresh axios instance for the update
    const freshAxios = axios.create({
      baseURL: '',
      timeout: 10000,
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
      transformRequest: [(data) => data],
    })

    // Add auth header if available
    const auth = localStorage.getItem('kt-auth-react-v')
    if (auth) {
      try {
        const authData = JSON.parse(auth)
        if (authData?.auth?.jwt) {
          freshAxios.defaults.headers.common['Authorization'] = `Bearer ${authData.auth.jwt}`
        }
      } catch (e) {
        console.warn('Could not parse auth data:', e)
      }
    }

    const response = await freshAxios.put<RequisitionMutationResponse>(UPDATE_URL, formData)

    console.log('Requisition updated successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error updating requisition:', error)
    console.error('Error message:', error.message)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)
    console.error('Request URL:', error.config?.url)

    if (error.response?.status === 400) {
      throw new Error('Error updating requisition')
    } else if (error.response?.status === 404) {
      throw new Error('Requisition not found')
    } else if (error.response?.status === 500) {
      throw new Error('Error updating requisition')
    } else {
      throw new Error('Error updating requisition')
    }
  }
}

export const deleteRequisition = async (requisitionId: number): Promise<void> => {
  try {
    await axios.delete(`${REQUISITIONS_URL}/${requisitionId}`)
  } catch (error: any) {
    console.error('Error deleting requisition:', error)
    throw new Error(error.response?.data?.message || 'Error deleting requisition')
  }
}

export const submitRequisition = async (requisitionId: number): Promise<Requisition> => {
  try {
    const response = await axios.post<Requisition>(`${REQUISITIONS_URL}/${requisitionId}/submit`)
    return response.data
  } catch (error: any) {
    console.error('Error submitting requisition:', error)
    throw new Error(error.response?.data?.message || 'Error submitting requisition')
  }
}

export const approveVesselRequisition = async (
  requisitionId: number,
  payload: ApproveVesselRequest
): Promise<Requisition> => {
  try {
    const response = await axios.post<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/approve-vessel`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error approving vessel requisition:', error)
    throw new Error(error.response?.data?.message || 'Error approving vessel requisition')
  }
}

export const approveShoreRequisition = async (
  requisitionId: number,
  payload: ApproveShoreRequest
): Promise<Requisition> => {
  try {
    const response = await axios.post<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/approve-shore`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error approving shore requisition:', error)
    throw new Error(error.response?.data?.message || 'Error approving shore requisition')
  }
}

export const requestRevision = async (
  requisitionId: number,
  comment?: string
): Promise<Requisition> => {
  try {
    const response = await axios.post<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/request-revision`,
      null,
      {params: comment ? {comment} : {}}
    )
    return response.data
  } catch (error: any) {
    console.error('Error requesting revision:', error)
    throw new Error(error.response?.data?.message || 'Error requesting revision')
  }
}

export const sendReworkToVessel = async (
  requisitionId: number,
  comment: string
): Promise<Requisition> => {
  try {
    const response = await axios.post<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/request-revision`,
      null,
      {params: {comment}}
    )
    return response.data
  } catch (error: any) {
    console.error('Error sending requisition for rework:', error)
    throw new Error(error.response?.data?.message || 'Error sending requisition for rework')
  }
}

export const rejectRequisitionFromCompany = async (
  requisitionId: number,
  cause: string
): Promise<Requisition> => {
  try {
    const response = await axios.post<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/reject`,
      null,
      {params: {cause}}
    )
    return response.data
  } catch (error: any) {
    console.error('Error rejecting requisition:', error)
    throw new Error(error.response?.data?.message || 'Error rejecting requisition')
  }
}

export const getRequisitionAttachments = async (requisitionId: number): Promise<FileRefDto[]> => {
  try {
    const response = await axios.get<FileRefDto[]>(
      `${REQUISITIONS_URL}/${requisitionId}/attachments`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching requisition attachments:', error)
    throw new Error(error.response?.data?.message || 'Error fetching requisition attachments')
  }
}

export const uploadRequisitionAttachments = async (
  requisitionId: number,
  files: File[]
): Promise<FileRefDto[]> => {
  try {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file)
    })

    const response = await axios.post<FileRefDto[]>(
      `${REQUISITIONS_URL}/${requisitionId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error uploading requisition attachments:', error)
    throw new Error(error.response?.data?.message || 'Error uploading requisition attachments')
  }
}

// ——— PORTS ——————————————————————————————————————————————————————————————————

export const getDepartments = async (
  includeInactive: boolean = false
): Promise<DepartmentMaster[]> => {
  try {
    const response = await axios.get<any>(DEPARTMENTS_URL, {params: {includeInactive}})
    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.content)) return raw.content
    if (Array.isArray(raw?.items)) return raw.items
    return []
  } catch (error: any) {
    console.error('Error fetching departments:', error)
    throw new Error(error.response?.data?.message || 'Error fetching departments')
  }
}

export const getSubDepartmentsByDepartment = async (
  departmentId: number,
  includeInactive: boolean = false
): Promise<SubDepartmentMaster[]> => {
  try {
    const response = await axios.get<any>(`${DEPARTMENTS_URL}/${departmentId}/subdepartments`, {
      params: {includeInactive},
    })
    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.content)) return raw.content
    if (Array.isArray(raw?.items)) return raw.items
    return []
  } catch (error: any) {
    console.error('Error fetching sub departments:', error)
    throw new Error(error.response?.data?.message || 'Error fetching sub departments')
  }
}

export const getPortsPage = async ({
  page = 0,
  size = 10,
  q,
}: {
  page?: number
  size?: number
  q?: string
} = {}): Promise<PageResponse<Port>> => {
  try {
    const response = await axios.get<PageResponse<any>>(PORTS_URL, {
      params: {page, size, ...(q?.trim() ? {q: q.trim()} : {})},
    })
    const mappedPorts: Port[] = (response.data.content || []).map((port) => ({
      id: port.id,
      name: port.main_port || port.name || 'Unknown Port',
      code: port.code || '',
      country: port.country || '',
      latitude: port.latitude,
      longitude: port.longitude,
      main_port: port.main_port,
    }))

    return {...response.data, content: mappedPorts}
  } catch (error: any) {
    console.error('Error fetching ports:', error)
    throw new Error(error.response?.data?.message || 'Error fetching ports')
  }
}

/** Complete ordered lookup for forms that must offer every port. */
export const getPorts = async (): Promise<Port[]> => {
  try {
    const response = await axios.get<any[]>(`${PORTS_URL}/all`)
    return response.data.map((port) => ({
      id: port.id,
      name: port.main_port || port.name || 'Unknown Port',
      code: port.code || '',
      country: port.country || '',
      latitude: port.latitude,
      longitude: port.longitude,
      main_port: port.main_port,
    }))
  } catch (error: any) {
    console.error('Error fetching all ports:', error)
    throw new Error(error.response?.data?.message || 'Error fetching all ports')
  }
}

export const getPortById = async (portId: number): Promise<Port> => {
  try {
    const response = await axios.get<Port>(`${PORTS_URL}/${portId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching port:', error)
    throw new Error(error.response?.data?.message || 'Error fetching port')
  }
}

export const createPort = async (portData: CreatePortRequest): Promise<Port> => {
  try {
    console.log('Creating port with data:', portData)
    const response = await axios.post<Port>(PORTS_URL, portData)
    console.log('Port created successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error creating port:', error)
    throw new Error(error.response?.data?.message || 'Error creating port')
  }
}

export const updatePort = async (id: number, portData: CreatePortRequest): Promise<Port> => {
  const response = await axios.put<Port>(`${PORTS_URL}/${id}`, portData)
  return response.data
}

export const deletePort = async (id: number): Promise<void> => {
  await axios.delete(`${PORTS_URL}/${id}`)
}

// ——— GOODS RECEIPTS ————————————————————————————————————————————————————————

export const getGoodsReceiptsPage = async (
  page: number = 0,
  size: number = 10,
  filters?: {companyId?: number; companyType?: string; vesselId?: number}
): Promise<GoodsReceiptPageResponse> => {
  try {
    console.log('Fetching goods receipts from:', GOODS_RECEIPTS_URL)
    const response = await axios.get<any>(GOODS_RECEIPTS_URL, {
      params: {
        page,
        size,
        ...(filters?.companyId ? {companyId: filters.companyId} : {}),
        ...(filters?.companyType ? {companyType: filters.companyType} : {}),
        ...(filters?.vesselId ? {vesselId: filters.vesselId} : {}),
      },
      headers: {
        Accept: 'application/json',
      },
    })
    console.log('Goods receipts fetched successfully:', response.data)
    return normalizePagedResponse<GoodsReceiptListRow>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching goods receipts:', error)
    throw procurementErr(error, 'Error fetching goods receipts')
  }
}

export const getGoodsReceipts = async (): Promise<GoodsReceiptListRow[]> => {
  const page = await getGoodsReceiptsPage(0, 1000)
  return page.content || []
}

export const getGoodsReceiptsByPo = async (poId: number): Promise<GoodsReceipt[]> => {
  try {
    const response = await axios.get<GoodsReceipt[]>(`${GOODS_RECEIPTS_URL}/by-po/${poId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching goods receipts by PO:', error)
    throw new Error(error.response?.data?.message || 'Error fetching goods receipts by PO')
  }
}

export const getReceivableGoodsReceiptPurchaseOrdersPage = async (
  page: number = 0,
  size: number = 25,
  q?: string,
  vesselId?: number
): Promise<PageResponse<GoodsReceiptReceivablePoSelectorRow>> => {
  try {
    const response = await axios.get<any>(`${GOODS_RECEIPTS_URL}/receivable-pos`, {
      params: {
        page,
        size,
        ...(q ? {q} : {}),
        ...(vesselId ? {vesselId} : {}),
      },
      headers: {
        Accept: 'application/json',
      },
    })
    return normalizePagedResponse<GoodsReceiptReceivablePoSelectorRow>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching receivable purchase orders for goods receipt:', error)
    throw procurementErr(error, 'Error fetching receivable purchase orders')
  }
}

export const getGoodsReceiptReceiptContext = async (
  poId: number
): Promise<GoodsReceiptReceiptContext> => {
  try {
    const response = await axios.get<GoodsReceiptReceiptContext>(
      `${GOODS_RECEIPTS_URL}/receipt-context/${poId}`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching goods receipt purchase order context:', error)
    throw procurementErr(error, 'Error fetching goods receipt purchase order context')
  }
}

export const getGoodsReceiptById = async (id: number): Promise<GoodsReceiptDetail> => {
  try {
    const response = await axios.get<GoodsReceiptDetail>(`${GOODS_RECEIPTS_URL}/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching goods receipt:', error)
    throw new Error(error.response?.data?.message || 'Error fetching goods receipt')
  }
}

export const createGoodsReceipt = async (
  payload: CreateGoodsReceiptRequest,
  photoFiles?: File[]
): Promise<GoodsReceipt> => {
  try {
    console.log('Creating goods receipt with payload:', JSON.stringify(payload, null, 2))
    console.log('Photo files to upload:', photoFiles?.length || 0)

    // Create FormData for multipart request
    const formData = new FormData()

    // Add basic fields
    formData.append('purchaseOrderId', payload.purchaseOrderId.toString())
    formData.append('receivedDate', payload.receivedDate)
    formData.append('receivedBy', payload.receivedBy)
    formData.append('vendorRating', payload.vendorRating.toString())
    formData.append('generalNotes', payload.generalNotes)

    // Add line items
    payload.lines.forEach((line, index) => {
      const inventoryId = line.inventoryItemId != null ? line.inventoryItemId.toString() : ''
      formData.append(`lines[${index}].inventoryItemId`, inventoryId)
      if (line.requisitionLineId != null) {
        formData.append(`lines[${index}].requisitionLineId`, line.requisitionLineId.toString())
      }
      formData.append(`lines[${index}].itemDescription`, line.itemDescription)
      formData.append(`lines[${index}].partNumber`, line.partNumber ?? '')
      formData.append(`lines[${index}].expectedQty`, line.expectedQty.toString())
      formData.append(`lines[${index}].receivedQty`, line.receivedQty.toString())
      formData.append(`lines[${index}].condition`, line.condition)
      if (line.notes) {
        formData.append(`lines[${index}].notes`, line.notes)
      }
    })

    // Add photo files
    if (photoFiles && photoFiles.length > 0) {
      photoFiles.forEach((file) => {
        formData.append('photoFiles', file)
      })
      console.log('Added', photoFiles.length, 'photo files to FormData')
    }

    console.log('FormData entries:')
    formData.forEach((value, key) => {
      console.log(`${key}: ${value}`)
    })

    const response = await axios.post<GoodsReceipt>(GOODS_RECEIPTS_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
      timeout: 30000,
    })

    console.log('Goods receipt created successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error creating goods receipt:', error)
    console.error('Error message:', error.message)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)

    if (error.response?.status === 400) {
      const validationErrors = error.response?.data?.errors || error.response?.data?.message
      throw new Error(`Validation error: ${JSON.stringify(validationErrors)}`)
    } else if (error.response?.status === 404) {
      throw new Error('API endpoint not found. Please check the URL.')
    } else if (error.response?.status === 500) {
      throw new Error('Error creating goods receipt')
    } else {
      throw new Error('Error creating goods receipt')
    }
  }
}

// ——— RFQ ————————————————————————————————————————————————————————————————————

export const getRFQById = async (rfqId: number): Promise<RFQ> => {
  try {
    const response = await axios.get<RFQ>(`${RFQS_URL}/${rfqId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching RFQ:', error)
    throw new Error(error.response?.data?.message || 'Error fetching RFQ')
  }
}

export const createRFQFromRequisition = async (
  requisitionId: number,
  payload: CreateRFQFromRequisitionRequest
): Promise<RFQ> => {
  try {
    console.log('Creating RFQ from requisition:', requisitionId, 'with payload:', payload)
    const response = await axios.post<RFQ>(`${RFQS_URL}/from-requisition/${requisitionId}`, payload)
    console.log('RFQ created successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error creating RFQ from requisition:', error)
    console.error('Error response:', error.response?.data)
    throw new Error(error.response?.data?.message || 'Error creating RFQ from requisition')
  }
}

export const updateRFQ = async (rfqId: number, payload: UpdateRFQRequest): Promise<RFQ> => {
  try {
    console.log('Updating RFQ:', rfqId, 'payload:', payload)
    const response = await axios.put<RFQ>(`${RFQS_URL}/${rfqId}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating RFQ:', error)
    throw new Error(error.response?.data?.message || 'Error updating RFQ')
  }
}

export const inviteVendorsToRFQ = async (
  rfqId: number,
  vendorCompanyMappingIds: number[],
  requisitionLineId?: number
): Promise<RFQ> => {
  try {
    console.log(
      'Inviting vendors to RFQ:',
      rfqId,
      'vendors:',
      vendorCompanyMappingIds,
      'lineId:',
      requisitionLineId
    )
    const payload = requisitionLineId
      ? [{vendorCompanyMappingIds, requisitionLineId}]
      : [{vendorCompanyMappingIds}]

    console.log('Invite payload:', payload)
    const response = await axios.post<RFQ>(`${RFQS_URL}/${rfqId}/invite`, payload)
    console.log('Vendors invited successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error inviting vendors:', error)
    console.error('Error response:', error.response?.data)
    throw new Error(error.response?.data?.message || 'Error inviting vendors to RFQ')
  }
}

// New function to invite vendors for multiple lines
export const inviteVendorsToRFQLines = async (
  rfqId: number,
  lineInvitations: Array<{requisitionLineId: number; vendorCompanyMappingIds: number[]}>
): Promise<RFQ> => {
  try {
    console.log('Inviting vendors to RFQ lines:', rfqId, 'invitations:', lineInvitations)

    // Send all line invitations in a single request
    const payload = lineInvitations.map((inv) => ({
      vendorCompanyMappingIds: inv.vendorCompanyMappingIds,
      requisitionLineId: inv.requisitionLineId,
    }))

    console.log('Invite lines payload:', payload)
    const response = await axios.post<RFQ>(`${RFQS_URL}/${rfqId}/invite`, payload)
    console.log('Vendors invited to lines successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error inviting vendors to lines:', error)
    console.error('Error response:', error.response?.data)
    throw new Error(error.response?.data?.message || 'Error inviting vendors to RFQ lines')
  }
}

export const searchRegions = async (query: string): Promise<RegionOption[]> => {
  if (!query.trim()) return []
  const response = await axios.get<RegionOption[]>(`${REGIONS_URL}/search`, {params: {query}})
  return Array.isArray(response.data) ? response.data : []
}

export const getRfqVendorCandidates = async (
  requisitionId: number,
  selectedRegionId?: number | null,
  q?: string
): Promise<RfqVendorCandidatesResponse> => {
  const response = await axios.get<RfqVendorCandidatesResponse>(
    `${RFQS_URL}/requisition/${requisitionId}/vendor-candidates`,
    {
      params: {
        ...(selectedRegionId != null ? {selectedRegionId} : {}),
        ...(q && q.trim() ? {q: q.trim()} : {}),
      },
    }
  )
  return {
    ...(response.data || {}),
    suggestedVendors: response.data?.suggestedVendors ?? [],
    otherVendors: response.data?.otherVendors ?? [],
    regionMatched: response.data?.regionMatched ?? false,
  }
}

export const getRFQsPage = async ({
  cgaid,
  status,
  vesselId,
  q,
  reference,
  categoryId,
  requisitionTitle,
  approvalPendingWith,
  tab,
  responseDueDateFrom,
  responseDueDateTo,
  page = 0,
  size = 10,
  sort,
}: RFQListQuery = {}): Promise<RFQPageResponse> => {
  try {
    const params: any = {}
    if (cgaid) params.cgaid = cgaid
    if (status) params.status = status
    if (vesselId !== null && vesselId !== undefined) params.vesselId = vesselId
    if (q && q.trim() !== '') params.q = q.trim()
    if (reference && reference.trim() !== '') params.reference = reference.trim()
    if (categoryId != null) params.categoryId = categoryId
    if (requisitionTitle && requisitionTitle.trim() !== '') {
      params.requisitionTitle = requisitionTitle.trim()
    }
    if (approvalPendingWith && approvalPendingWith.trim() !== '') {
      params.approvalPendingWith = approvalPendingWith.trim()
    }
    if (tab) params.tab = tab
    if (responseDueDateFrom) params.responseDueDateFrom = responseDueDateFrom
    if (responseDueDateTo) params.responseDueDateTo = responseDueDateTo
    params.page = page
    params.size = size
    if (Array.isArray(sort) && sort.length > 0) params.sort = sort

    const response = await axios.get<any>(RFQS_URL, {
      params,
      paramsSerializer: serializeProcurementQueryParams,
      headers: {
        Accept: 'application/json',
      },
    })

    return normalizePagedResponse<RFQListSummary>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching RFQs:', error)
    throw procurementErr(error, 'Error fetching RFQs')
  }
}

export const getRFQs = async (
  cgaid?: number,
  status?: string,
  vesselId?: number | null,
  q?: string
): Promise<RFQ[]> => {
  const response = await getRFQsPage({cgaid, status, vesselId, q, page: 0, size: 200})
  return response.content || []
}

export const getNextRFQCode = async (): Promise<string> => {
  try {
    const response = await axios.get<NextCodeResponse>(`${RFQS_URL}/next-code`)
    return response.data.nextCode
  } catch (error: any) {
    console.error('Error fetching next RFQ code:', error)
    // Return a fallback code if the API doesn't support this endpoint
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
    const randomNum = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0')
    return `RFQ-${timestamp}-${randomNum}`
  }
}

export const getRFQVendors = async (rfqId: number): Promise<number[]> => {
  try {
    const rfq = await getRFQById(rfqId)
    return rfq.invitedVendorIds || []
  } catch (error: any) {
    console.error('Error fetching RFQ vendors:', error)
    return []
  }
}

export const sendRFQ = async (rfqId: number): Promise<RFQ> => {
  try {
    const response = await axios.post<RFQ>(`${RFQS_URL}/${rfqId}/send`)
    return response.data
  } catch (error: any) {
    console.error('Error sending RFQ:', error)
    throw procurementErr(error, 'Error sending RFQ')
  }
}

export const deleteRFQ = async (rfqId: number): Promise<void> => {
  try {
    await axios.delete(`${RFQS_URL}/${rfqId}`)
  } catch (error: any) {
    console.error('Error deleting RFQ:', error)
    throw new Error(error.response?.data?.message || 'Error deleting RFQ')
  }
}

// ——— QUOTATIONS ————————————————————————————————————————————————————————————

export const getQuotationsByRFQPage = async (
  rfqId: number,
  page: number = 0,
  size: number = 10
): Promise<PageResponse<any>> => {
  try {
    const response = await axios.get(`${QUOTATIONS_URL}/rfq/${rfqId}`, {
      params: {page, size},
    })
    return normalizePagedResponse<any>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching quotations:', error)
    throw new Error(error.response?.data?.message || 'Error fetching quotations')
  }
}

export const getQuotationsByRFQ = async (rfqId: number): Promise<any[]> => {
  const response = await getQuotationsByRFQPage(rfqId, 0, 200)
  return response.content || []
}

export const getQuotationComparison = async (
  rfqId: number
): Promise<QuotationComparisonResponse> => {
  try {
    const response = await axios.get<QuotationComparisonResponse>(
      `${QUOTATIONS_URL}/rfq/${rfqId}/comparison`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching quotation comparison:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching quotation comparison'
    )
  }
}

export const getQuotationById = async (quotationId: number): Promise<any> => {
  try {
    const response = await axios.get(`${QUOTATIONS_URL}/${quotationId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching quotation detail:', error)
    throw new Error(error.response?.data?.message || 'Error fetching quotation detail')
  }
}

export const getMyQuotationForRfq = async (rfqId: number): Promise<any | null> => {
  try {
    const response = await axios.get(`${QUOTATIONS_URL}/rfq/${rfqId}/me`)
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    console.error('Error fetching current vendor quotation:', error)
    const wrapped: any = new Error(
      error.response?.data?.message || 'Error fetching vendor quotation'
    )
    wrapped.status = error?.response?.status
    throw wrapped
  }
}

export const getQuotationCurrencyContext = async (
  rfqId: number
): Promise<QuotationCurrencyContext> => {
  try {
    const response = await axios.get<QuotationCurrencyContext>(
      `${QUOTATIONS_URL}/rfq/${rfqId}/currency-context`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching quotation currency context:', error)
    const wrapped: any = new Error(
      error.response?.data?.message || 'Error fetching quotation currency context'
    )
    wrapped.status = error?.response?.status
    throw wrapped
  }
}

const appendQuotationFormData = (payload: any, files?: File[]) => {
  const formData = new FormData()
  formData.append(
    'data',
    new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    })
  )
  if (files && files.length > 0) {
    files.forEach((file) => formData.append('files', file))
  }
  return formData
}

export const saveQuotationDraft = async (payload: any, files?: File[]): Promise<any> => {
  try {
    const formData = appendQuotationFormData(payload, files)
    const response = await axios.post(`${QUOTATIONS_URL}/draft`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving quotation draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving quotation draft')
  }
}

export const submitQuotation = async (payload: any, files?: File[]): Promise<void> => {
  try {
    const formData = appendQuotationFormData(payload, files)
    await axios.post(`${QUOTATIONS_URL}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  } catch (error: any) {
    console.error('Error submitting quotation:', error)
    throw new Error(error.response?.data?.message || 'Error submitting quotation')
  }
}

export const getVesselQuotationFeedPage = async (
  page: number = 0,
  size: number = 10
): Promise<PageResponse<any>> => {
  try {
    const response = await axios.get(`${QUOTATIONS_URL}/vessel-feed`, {
      params: {page, size},
    })
    return normalizePagedResponse<any>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching vessel quotation feed:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel quotations')
  }
}

export const getVesselQuotationFeed = async (): Promise<any[]> => {
  const response = await getVesselQuotationFeedPage(0, 200)
  return response.content || []
}

export interface QuotationAwardLineAssignment {
  requisitionLineId: number
  assignedQuantity: number
  accountCodeId?: number | null
  accountCode?: string
  subaccountCodeId?: number | null
  subaccountCode?: string
}

export const selectQuotation = async (
  quotationId: number,
  assignedQuantity?: number,
  assignedLines?: QuotationAwardLineAssignment[]
): Promise<any> => {
  try {
    const normalizedLines = (assignedLines || [])
      .filter((line) => line && line.requisitionLineId != null && Number(line.assignedQuantity) > 0)
      .map((line) => ({
        requisitionLineId: Number(line.requisitionLineId),
        assignedQuantity: Math.max(0, Math.floor(Number(line.assignedQuantity))),
        accountCodeId:
          line.accountCodeId !== null && line.accountCodeId !== undefined
            ? Number(line.accountCodeId)
            : undefined,
        accountCode: line.accountCode,
        subaccountCodeId:
          line.subaccountCodeId !== null && line.subaccountCodeId !== undefined
            ? Number(line.subaccountCodeId)
            : undefined,
        subaccountCode: line.subaccountCode,
      }))

    const lineTotal = normalizedLines.reduce((sum, line) => sum + line.assignedQuantity, 0)
    const resolvedAssignedQty =
      assignedQuantity !== undefined && assignedQuantity !== null ? assignedQuantity : lineTotal

    if (
      resolvedAssignedQty === undefined ||
      resolvedAssignedQty === null ||
      resolvedAssignedQty <= 0
    ) {
      throw new Error('Assigned quantity is required to select a quotation')
    }

    const payload: any = {
      // Backend expects requiredQuantity in the DTO for selection logic/validation
      requiredQuantity: resolvedAssignedQty,
      // Keep snapshot fields in sync
      actualQuantityAssignedToVendor: resolvedAssignedQty,
    }
    if (normalizedLines.length > 0) {
      payload.lines = normalizedLines
    }

    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/select`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error selecting quotation:', error)
    throw new Error(error.response?.data?.message || 'Error selecting quotation')
  }
}

export const updateQuotationAward = async (
  quotationId: number,
  assignedQuantity: number,
  assignedLines?: QuotationAwardLineAssignment[]
): Promise<any> => {
  try {
    const normalizedLines = (assignedLines || [])
      .filter((line) => line && line.requisitionLineId != null && Number(line.assignedQuantity) > 0)
      .map((line) => ({
        requisitionLineId: Number(line.requisitionLineId),
        assignedQuantity: Math.max(0, Math.floor(Number(line.assignedQuantity))),
        accountCodeId:
          line.accountCodeId !== null && line.accountCodeId !== undefined
            ? Number(line.accountCodeId)
            : undefined,
        accountCode: line.accountCode,
        subaccountCodeId:
          line.subaccountCodeId !== null && line.subaccountCodeId !== undefined
            ? Number(line.subaccountCodeId)
            : undefined,
        subaccountCode: line.subaccountCode,
      }))

    const lineTotal = normalizedLines.reduce((sum, line) => sum + line.assignedQuantity, 0)
    const resolvedAssignedQty =
      assignedQuantity !== undefined && assignedQuantity !== null ? assignedQuantity : lineTotal

    if (
      resolvedAssignedQty === undefined ||
      resolvedAssignedQty === null ||
      resolvedAssignedQty <= 0
    ) {
      throw new Error('Assigned quantity is required to update award')
    }

    const payload: any = {
      actualQuantityAssignedToVendor: resolvedAssignedQty,
      requiredQuantity: resolvedAssignedQty,
    }
    if (normalizedLines.length > 0) {
      payload.lines = normalizedLines
    }

    const response = await axios.patch(`${QUOTATIONS_URL}/${quotationId}/award`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating quotation award:', error)
    throw new Error(error.response?.data?.message || 'Error updating quotation award')
  }
}

export const closeQuotation = async (quotationId: number): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/close`)
    return response.data
  } catch (error: any) {
    console.error('Error closing quotation:', error)
    throw new Error(error.response?.data?.message || 'Error closing quotation')
  }
}

// ——— VENDORS ————————————————————————————————————————————————————————————————

const VENDOR_CATEGORIES_URL = `${API_URL}/vendor-categories`

export const getVendorCategories = async (): Promise<Array<{id: number; name: string}>> => {
  try {
    const response = await axios.get<Array<{id: number; name: string}>>(VENDOR_CATEGORIES_URL)
    return response.data
  } catch (error: any) {
    console.error('Error fetching vendor categories:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vendor categories')
  }
}

export const getVendors = async (
  page: number = 0,
  size: number = 20,
  companyGroupAdminId?: number
): Promise<VendorPageResponse> => {
  try {
    const response = await axios.get<any>(VENDORS_URL, {
      params: {page, size, ...(companyGroupAdminId ? {companyGroupAdminId} : {})},
    })

    // Handle both array and paginated responses
    if (Array.isArray(response.data)) {
      return {
        content: response.data,
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: {sorted: false, empty: true, unsorted: true},
          offset: page * size,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: 1,
        totalElements: response.data.length,
        size: response.data.length,
        number: page,
        sort: {sorted: false, empty: true, unsorted: true},
        first: true,
        numberOfElements: response.data.length,
        empty: response.data.length === 0,
      }
    } else if (Array.isArray(response.data?.content)) {
      return response.data
    } else if (Array.isArray(response.data?.items)) {
      const items = response.data.items
      const resolvedPage = Number(response.data?.page ?? page)
      const resolvedSize = Number(response.data?.size ?? size)
      const totalElements = Number(response.data?.total ?? items.length)
      const totalPages = resolvedSize > 0 ? Math.max(1, Math.ceil(totalElements / resolvedSize)) : 1
      return {
        content: items,
        pageable: {
          pageNumber: resolvedPage,
          pageSize: resolvedSize,
          sort: {sorted: false, empty: true, unsorted: true},
          offset: resolvedPage * resolvedSize,
          paged: true,
          unpaged: false,
        },
        last: resolvedPage >= totalPages - 1,
        totalPages,
        totalElements,
        size: resolvedSize,
        number: resolvedPage,
        sort: {sorted: false, empty: true, unsorted: true},
        first: resolvedPage === 0,
        numberOfElements: items.length,
        empty: items.length === 0,
      }
    } else {
      return {
        content: [],
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: {sorted: false, empty: true, unsorted: true},
          offset: 0,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: 0,
        totalElements: 0,
        size: 0,
        number: page,
        sort: {sorted: false, empty: true, unsorted: true},
        first: true,
        numberOfElements: 0,
        empty: true,
      }
    }
  } catch (error: any) {
    console.error('Error fetching vendors:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vendors')
  }
}

export const getVendorById = async (vendorId: number): Promise<Vendor> => {
  try {
    const response = await axios.get<Vendor>(`${VENDORS_URL}/${vendorId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching vendor:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vendor')
  }
}

export const createVendor = async (payload: CreateVendorRequest): Promise<Vendor> => {
  try {
    const statusToTag: Record<string, string> = {
      Preferred: 'PREFERRED',
      Approved: 'NORMAL',
      'Under Review': 'NORMAL',
      Blocklisted: 'BLOCKED',
    }
    const response = await axios.post<Vendor>(VENDORS_URL, {
      name: payload.name,
      email: payload.email,
      contactNo: payload.phone || payload.contactPerson || '',
      address: payload.address,
      paymentTerms: payload.paymentTerms,
      qualityRating: payload.qualityRating,
      supplyRating: payload.supplyRating,
      profileNotes: payload.profileNotes,
      tag: statusToTag[payload.status] || 'NORMAL',
    })
    return response.data
  } catch (error: any) {
    console.error('Error creating vendor:', error)
    throw new Error(error.response?.data?.message || 'Error creating vendor')
  }
}

export const updateVendor = async (
  vendorId: number,
  payload: UpdateVendorRequest
): Promise<Vendor> => {
  try {
    const statusToTag: Record<string, string> = {
      Preferred: 'PREFERRED',
      Approved: 'NORMAL',
      'Under Review': 'NORMAL',
      Blocklisted: 'BLOCKED',
    }
    const response = await axios.put<Vendor>(`${VENDORS_URL}/${vendorId}`, {
      name: payload.name,
      email: payload.email,
      contactNo: payload.phone,
      address: payload.address,
      paymentTerms: payload.paymentTerms,
      qualityRating: payload.qualityRating,
      supplyRating: payload.supplyRating,
      profileNotes: payload.profileNotes,
      tag: payload.status ? statusToTag[payload.status] || 'NORMAL' : undefined,
    })
    return response.data
  } catch (error: any) {
    console.error('Error updating vendor:', error)
    throw new Error(error.response?.data?.message || 'Error updating vendor')
  }
}

export const deleteVendor = async (vendorId: number): Promise<void> => {
  try {
    await axios.delete(`${VENDORS_URL}/${vendorId}`)
  } catch (error: any) {
    console.error('Error deleting vendor:', error)
    throw new Error(error.response?.data?.message || 'Error deleting vendor')
  }
}

export const addItemsToRequisition = async (
  requisitionId: number,
  payload: {
    itemName: string
    quantity: number
    uom: string
    estimatedUnitPrice: number
  }
): Promise<Requisition> => {
  try {
    const response = await axios.put<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/add-items`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error adding items to requisition:', error)
    throw new Error(error.response?.data?.message || 'Error adding items to requisition')
  }
}

// ——— INVENTORY CATEGORIES ——————————————————————————————————————————————————

export const getInventoryCategories = async (
  cgaId: number,
  vesselId?: number,
  vesselType?: string
): Promise<InventoryCategory[]> => {
  try {
    const params = new URLSearchParams()
    if (cgaId) params.append('cgaId', cgaId.toString())
    if (vesselId) params.append('vesselId', vesselId.toString())
    if (vesselType) params.append('vesselType', vesselType)

    const response = await axios.get<InventoryCategory[]>(
      `${INVENTORY_CATEGORIES_URL}?${params.toString()}`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching inventory categories:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory categories')
  }
}

export const getInventoryCategoriesByVesselType = async (
  cgaId: number,
  vesselType: string
): Promise<InventoryCategory[]> => {
  try {
    const response = await axios.get<InventoryCategory[]>(
      `${INVENTORY_CATEGORIES_URL}?cgaId=${cgaId}&vesselType=${vesselType}`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching inventory categories by vessel type:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching inventory categories by vessel type'
    )
  }
}

export const getInventoryCategoriesForResolvedTenant = async (
  cgaId?: number
): Promise<InventoryCategory[]> => {
  try {
    const params = new URLSearchParams()
    if (cgaId) params.append('cgaId', cgaId.toString())

    const response = await axios.get<InventoryCategory[]>(
      `${INVENTORY_CATEGORIES_URL}${params.toString() ? `?${params.toString()}` : ''}`
    )
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
      ? response.data
      : []
    return rows
  } catch (error: any) {
    console.error('Error fetching inventory categories for resolved tenant:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory categories')
  }
}

export const getAllInventoryCategoriesForCompany = async (
  cgaId: number,
  vesselId?: number,
  vesselType?: string
): Promise<InventoryCategory[]> => {
  try {
    const params = new URLSearchParams()
    if (cgaId) params.append('cgaId', cgaId.toString())
    if (vesselId) params.append('vesselId', vesselId.toString())
    if (vesselType) params.append('vesselType', vesselType)

    const response = await axios.get<InventoryCategory[]>(
      `${INVENTORY_CATEGORIES_URL}${params.toString() ? `?${params.toString()}` : ''}`
    )
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
      ? response.data
      : []
    return rows
  } catch (error: any) {
    console.error('Error fetching inventory categories for company:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory categories')
  }
}

// Function to load categories for all companies (for superadmin)
export const getAllInventoryCategoriesForAllCompanies = async (): Promise<InventoryCategory[]> => {
  try {
    const response = await axios.get<InventoryCategory[]>(INVENTORY_CATEGORIES_URL)
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
      ? response.data
      : []
    return rows
  } catch (error: any) {
    console.error('Error loading categories for all companies:', error)
    throw new Error('Error fetching inventory categories for all companies')
  }
}

export const createInventoryCategory = async (payload: {
  name: string
  cgaid: {id: number}
  vesselId?: number
  vesselType?: string
}): Promise<InventoryCategory> => {
  try {
    const response = await axios.post<InventoryCategory>(INVENTORY_CATEGORIES_CREATE_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating inventory category:', error)
    throw new Error(error.response?.data?.message || 'Error creating inventory category')
  }
}

export const updateInventoryCategory = async (
  id: number,
  payload: {
    name: string
    cgaid: {id: number}
    caid: {id: number | null}
    vesselId?: number
    vesselType?: string
  }
): Promise<InventoryCategory> => {
  try {
    const response = await axios.put<InventoryCategory>(
      `${INVENTORY_CATEGORIES_CREATE_URL}/${id}`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating inventory category:', error)
    throw new Error(error.response?.data?.message || 'Error updating inventory category')
  }
}

export const deleteInventoryCategory = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${INVENTORY_CATEGORIES_CREATE_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting inventory category:', error)
    throw procurementErr(error, 'Error deleting inventory category')
  }
}

// ——— INVENTORY ITEM HEADS ——————————————————————————————————————————————————

export const getInventoryItemHeads = async (
  vesselId?: number,
  cgaId?: number
): Promise<InventoryItemHead[]> => {
  try {
    const params = new URLSearchParams()
    if (vesselId) params.append('vesselId', String(vesselId))
    if (cgaId) params.append('cgaId', String(cgaId))
    const query = params.toString()
    const url = query ? `${INVENTORY_ITEM_HEADS_URL}?${query}` : INVENTORY_ITEM_HEADS_URL
    const response = await axios.get<InventoryItemHead[]>(url)
    return response.data
  } catch (error: any) {
    console.error('Error fetching inventory item heads:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory item heads')
  }
}

export const createInventoryItemHead = async (
  payload: CreateInventoryItemHeadRequest
): Promise<InventoryItemHead> => {
  try {
    const response = await axios.post<InventoryItemHead>(INVENTORY_ITEM_HEADS_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating inventory item head:', error)
    throw new Error(error.response?.data?.message || 'Error creating inventory item head')
  }
}

export const updateInventoryItemHead = async (
  id: number,
  payload: CreateInventoryItemHeadRequest
): Promise<InventoryItemHead> => {
  try {
    const response = await axios.put<InventoryItemHead>(
      `${INVENTORY_ITEM_HEADS_URL}/${id}`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating inventory item head:', error)
    throw new Error(error.response?.data?.message || 'Error updating inventory item head')
  }
}

export const deleteInventoryItemHead = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${INVENTORY_ITEM_HEADS_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting inventory item head:', error)
    throw procurementErr(error, 'Error deleting inventory item head')
  }
}

// ——— INVENTORY ITEM SUB HEADS ——————————————————————————————————————————————————

export const getInventoryItemSubHeads = async (
  vesselId?: number,
  headId?: number,
  cgaId?: number
): Promise<InventoryItemSubHead[]> => {
  try {
    const params = new URLSearchParams()
    if (vesselId) params.append('vesselId', String(vesselId))
    if (headId) params.append('headId', String(headId))
    if (cgaId) params.append('cgaId', String(cgaId))
    const query = params.toString()
    const url = query ? `${INVENTORY_ITEM_SUBHEADS_URL}?${query}` : INVENTORY_ITEM_SUBHEADS_URL
    const response = await axios.get<InventoryItemSubHead[]>(url)
    return response.data
  } catch (error: any) {
    console.error('Error fetching inventory item sub heads:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory item sub heads')
  }
}

export const getInventoryAddresses = async (
  cgaId?: number,
  activeOnly: boolean = false
): Promise<InventoryAddress[]> => {
  try {
    const params = new URLSearchParams()
    if (cgaId) params.append('cgaId', String(cgaId))
    if (activeOnly) params.append('activeOnly', 'true')
    const query = params.toString()
    const url = query ? `${INVENTORY_ADDRESSES_URL}?${query}` : INVENTORY_ADDRESSES_URL
    const response = await axios.get<InventoryAddress[]>(url)
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching inventory addresses:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory addresses')
  }
}

export const createInventoryAddress = async (
  payload: CreateInventoryAddressRequest
): Promise<InventoryAddress> => {
  try {
    const response = await axios.post<InventoryAddress>(INVENTORY_ADDRESSES_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating inventory address:', error)
    throw new Error(error.response?.data?.message || 'Error creating inventory address')
  }
}

export const updateInventoryAddress = async (
  id: number,
  payload: CreateInventoryAddressRequest
): Promise<InventoryAddress> => {
  try {
    const response = await axios.put<InventoryAddress>(`${INVENTORY_ADDRESSES_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating inventory address:', error)
    throw new Error(error.response?.data?.message || 'Error updating inventory address')
  }
}

export const deleteInventoryAddress = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${INVENTORY_ADDRESSES_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting inventory address:', error)
    throw procurementErr(error, 'Error deleting inventory address')
  }
}

export const getTermsConditionsTemplates = async (params?: {
  search?: string
  activeOnly?: boolean
  scopeType?: string
  rfqId?: number
}): Promise<ProcurementTermsConditionsTemplate[]> => {
  try {
    const response = await axios.get<ProcurementTermsConditionsTemplate[]>(
      TERMS_CONDITIONS_LIBRARY_URL,
      {
        params,
      }
    )
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching terms templates:', error)
    throw new Error(error.response?.data?.message || 'Error fetching terms templates')
  }
}

export const createTermsConditionsTemplate = async (
  payload: Partial<ProcurementTermsConditionsTemplate>
): Promise<ProcurementTermsConditionsTemplate> => {
  try {
    const response = await axios.post<ProcurementTermsConditionsTemplate>(
      TERMS_CONDITIONS_LIBRARY_URL,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating terms template:', error)
    throw new Error(error.response?.data?.message || 'Error creating terms template')
  }
}

export const updateTermsConditionsTemplate = async (
  id: number,
  payload: Partial<ProcurementTermsConditionsTemplate>
): Promise<ProcurementTermsConditionsTemplate> => {
  try {
    const response = await axios.put<ProcurementTermsConditionsTemplate>(
      `${TERMS_CONDITIONS_LIBRARY_URL}/${id}`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating terms template:', error)
    throw new Error(error.response?.data?.message || 'Error updating terms template')
  }
}

export const deleteTermsConditionsTemplate = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${TERMS_CONDITIONS_LIBRARY_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting terms template:', error)
    throw procurementErr(error, 'Error deleting terms template')
  }
}

export const getApprovalPolicies = async (params?: {
  search?: string
  moduleType?: string
  companyGroupAdminId?: number
  companyAdminId?: number
  vesselId?: number
  active?: boolean
  currency?: string
}): Promise<ApprovalPolicy[]> => {
  try {
    const response = await axios.get<ApprovalPolicy[]>(APPROVAL_POLICIES_URL, {params})
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching approval policies:', error)
    throw new Error(error.response?.data?.message || 'Error fetching approval policies')
  }
}

export const createApprovalPolicy = async (
  payload: Partial<ApprovalPolicy>
): Promise<ApprovalPolicy> => {
  try {
    const response = await axios.post<ApprovalPolicy>(APPROVAL_POLICIES_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating approval policy:', error)
    throw new Error(error.response?.data?.message || 'Error creating approval policy')
  }
}

export const updateApprovalPolicy = async (
  id: number,
  payload: Partial<ApprovalPolicy>
): Promise<ApprovalPolicy> => {
  try {
    const response = await axios.put<ApprovalPolicy>(`${APPROVAL_POLICIES_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating approval policy:', error)
    throw new Error(error.response?.data?.message || 'Error updating approval policy')
  }
}

export const patchApprovalPolicyActive = async (
  id: number,
  active: boolean
): Promise<ApprovalPolicy> => {
  try {
    const response = await axios.patch<ApprovalPolicy>(
      `${APPROVAL_POLICIES_URL}/${id}/active`,
      null,
      {
        params: {active},
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating approval policy active flag:', error)
    throw new Error(error.response?.data?.message || 'Error updating approval policy active flag')
  }
}

export const cloneApprovalPolicy = async (id: number): Promise<ApprovalPolicy> => {
  try {
    const response = await axios.post<ApprovalPolicy>(`${APPROVAL_POLICIES_URL}/${id}/clone`)
    return response.data
  } catch (error: any) {
    console.error('Error cloning approval policy:', error)
    throw new Error(error.response?.data?.message || 'Error cloning approval policy')
  }
}

export const deleteApprovalPolicy = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${APPROVAL_POLICIES_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting approval policy:', error)
    throw procurementErr(error, 'Error deleting approval policy')
  }
}

export const getApprovalInstanceByDocument = async (
  moduleType: string,
  documentId: number
): Promise<ApprovalInstance | null> => {
  try {
    const response = await axios.get<ApprovalInstance | null>(
      `${APPROVAL_INSTANCES_URL}/by-document`,
      {
        params: {moduleType, documentId},
      }
    )
    return response.data || null
  } catch (error: any) {
    if (error.response?.status === 404) return null
    console.error('Error fetching approval instance by document:', error)
    throw new Error(error.response?.data?.message || 'Error fetching approval instance')
  }
}

export const getApprovalInstanceHistory = async (
  moduleType: string,
  documentId: number
): Promise<ApprovalInstance[]> => {
  try {
    const response = await axios.get<ApprovalInstance[]>(`${APPROVAL_INSTANCES_URL}/history`, {
      params: {moduleType, documentId},
    })
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching approval history:', error)
    throw new Error(error.response?.data?.message || 'Error fetching approval history')
  }
}

export const getPendingApprovalInstances = async (): Promise<ApprovalInstance[]> => {
  try {
    const response = await axios.get<ApprovalInstance[]>(`${APPROVAL_INSTANCES_URL}/pending`)
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching pending approvals:', error)
    throw new Error(error.response?.data?.message || 'Error fetching pending approvals')
  }
}

export const getApprovalInstanceAvailableActions = async (
  id: number
): Promise<ApprovalAvailableActions> => {
  try {
    const response = await axios.get<ApprovalAvailableActions>(
      `${APPROVAL_INSTANCES_URL}/${id}/available-actions`
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching approval instance available actions:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching approval instance available actions'
    )
  }
}

export const getApprovalDocumentAvailableActions = async (
  moduleType: string,
  documentId: number
): Promise<ApprovalAvailableActions> => {
  try {
    const response = await axios.get<ApprovalAvailableActions>(
      `${APPROVAL_INSTANCES_URL}/by-document/available-actions`,
      {
        params: {moduleType, documentId},
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching approval document available actions:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching approval document available actions'
    )
  }
}

export const getApprovalEligibility = async (
  moduleType: string,
  documentId: number
): Promise<ApprovalEligibility> => {
  try {
    const response = await axios.get<ApprovalEligibility>(
      `${API_URL}/procurement/approval-eligibility`,
      {
        params: {moduleType, documentId},
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching approval eligibility:', error)
    throw new Error(error.response?.data?.message || 'Error fetching approval eligibility')
  }
}

export const getPurchaseOrderApprovalSummaries = async (
  ids: number[]
): Promise<PurchaseOrderApprovalSummary[]> => {
  try {
    if (!ids.length) {
      return []
    }
    const response = await axios.get<PurchaseOrderApprovalSummary[]>(`${PURCHASE_ORDERS_URL}/approval-summaries`, {
      params: {ids},
      paramsSerializer: (params) => serializeProcurementQueryParams(params as Record<string, unknown>),
    })
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching purchase order approval summaries:', error)
    throw new Error(error.response?.data?.message || 'Error fetching purchase order approval summaries')
  }
}

export const submitRfqForApproval = async (id: number): Promise<any> => {
  try {
    const response = await axios.post<any>(`${RFQS_URL}/${id}/approval/submit`)
    return response.data
  } catch (error: any) {
    console.error('Error submitting RFQ for approval:', error)
    throw new Error(error.response?.data?.message || 'Error submitting RFQ for approval')
  }
}

export const submitPurchaseOrderForApproval = async (id: number): Promise<any> => {
  try {
    const response = await axios.post<ApprovalInstance>(
      `${PURCHASE_ORDERS_URL}/${id}/approval/submit`
    )
    return response.data
  } catch (error: any) {
    console.error('Error submitting PO for approval:', error)
    throw new Error(error.response?.data?.message || 'Error submitting PO for approval')
  }
}

export const submitInvoiceForApproval = async (id: number): Promise<any> => {
  try {
    const response = await axios.post<any>(`${INVOICES_URL}/${id}/approval/submit`)
    return response.data
  } catch (error: any) {
    console.error('Error submitting invoice for approval:', error)
    throw new Error(error.response?.data?.message || 'Error submitting invoice for approval')
  }
}

export const approveRfqApproval = async (id: number, remark?: string): Promise<any> => {
  const response = await axios.post(`${RFQS_URL}/${id}/approval/approve`, remark ? {remark} : {})
  return response.data
}

export const rejectRfqApproval = async (id: number, remark: string): Promise<any> => {
  const response = await axios.post(`${RFQS_URL}/${id}/approval/reject`, {remark})
  return response.data
}

export const reworkRfqApproval = async (id: number, remark: string): Promise<any> => {
  const response = await axios.post(`${RFQS_URL}/${id}/approval/rework`, {remark})
  return response.data
}

export const cancelRfqApproval = async (id: number, remark?: string): Promise<any> => {
  const response = await axios.post(`${RFQS_URL}/${id}/approval/cancel`, remark ? {remark} : {})
  return response.data
}

export const approveInvoiceApproval = async (id: number, remark?: string): Promise<any> => {
  const response = await axios.post(
    `${INVOICES_URL}/${id}/approval/approve`,
    remark ? {remark} : {}
  )
  return response.data
}

export const rejectInvoiceApproval = async (id: number, remark: string): Promise<any> => {
  const response = await axios.post(`${INVOICES_URL}/${id}/approval/reject`, {remark})
  return response.data
}

export const reworkInvoiceApproval = async (id: number, remark: string): Promise<any> => {
  const response = await axios.post(`${INVOICES_URL}/${id}/approval/rework`, {remark})
  return response.data
}

export const cancelInvoiceApproval = async (id: number, remark?: string): Promise<any> => {
  const response = await axios.post(`${INVOICES_URL}/${id}/approval/cancel`, remark ? {remark} : {})
  return response.data
}

export const getApprovalLimits = async (params?: {
  companyGroupAdminId?: number
  moduleType?: string
  currency?: string
  search?: string
}): Promise<ApprovalLimit[]> => {
  const response = await axios.get<ApprovalLimit[]>(`${API_URL}/procurement/approval-limits`, {
    params,
  })
  return response.data || []
}

export const createApprovalLimit = async (
  payload: Partial<ApprovalLimit>
): Promise<ApprovalLimit> => {
  const response = await axios.post<ApprovalLimit>(
    `${API_URL}/procurement/approval-limits`,
    payload
  )
  return response.data
}

export const updateApprovalLimit = async (
  id: number,
  payload: Partial<ApprovalLimit>
): Promise<ApprovalLimit> => {
  const response = await axios.put<ApprovalLimit>(
    `${API_URL}/procurement/approval-limits/${id}`,
    payload
  )
  return response.data
}

export const deleteApprovalLimit = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/procurement/approval-limits/${id}`)
}

export const setApprovalLimitActive = async (
  id: number,
  active: boolean
): Promise<ApprovalLimit> => {
  const response = await axios.patch<ApprovalLimit>(
    `${API_URL}/procurement/approval-limits/${id}/active`,
    null,
    {
      params: {active},
    }
  )
  return response.data
}

export const getCurrencyExchangeRates = async (params?: {
  sourceCurrencyCode?: string
  targetCurrencyCode?: string
  sourceType?: CurrencyRateSourceType
  companyGroupAdminId?: number
  vendorId?: number
  active?: boolean
}): Promise<CurrencyExchangeRate[]> => {
  try {
    const response = await axios.get<CurrencyExchangeRate[]>(CURRENCY_EXCHANGE_RATES_URL, {params})
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching currency exchange rates:', error)
    throw new Error(error.response?.data?.message || 'Error fetching currency exchange rates')
  }
}

export const getCurrencyExchangeRate = async (id: number): Promise<CurrencyExchangeRate> => {
  try {
    const response = await axios.get<CurrencyExchangeRate>(`${CURRENCY_EXCHANGE_RATES_URL}/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching currency exchange rate:', error)
    throw new Error(error.response?.data?.message || 'Error fetching currency exchange rate')
  }
}

export const createCurrencyExchangeRate = async (
  payload: CurrencyExchangeRate
): Promise<CurrencyExchangeRate> => {
  try {
    const response = await axios.post<CurrencyExchangeRate>(CURRENCY_EXCHANGE_RATES_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating currency exchange rate:', error)
    throw new Error(error.response?.data?.message || 'Error creating currency exchange rate')
  }
}

export const updateCurrencyExchangeRate = async (
  id: number,
  payload: CurrencyExchangeRateUpsertRequest
): Promise<CurrencyExchangeRate> => {
  try {
    const response = await axios.put<CurrencyExchangeRate>(
      `${CURRENCY_EXCHANGE_RATES_URL}/${id}`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating currency exchange rate:', error)
    throw new Error(error.response?.data?.message || 'Error updating currency exchange rate')
  }
}

export const upsertLatestCurrencyExchangeRate = async (
  payload: CurrencyExchangeRateUpsertRequest
): Promise<CurrencyExchangeRate> => {
  try {
    const response = await axios.put<CurrencyExchangeRate>(
      `${CURRENCY_EXCHANGE_RATES_URL}/latest`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error upserting latest currency exchange rate:', error)
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        'Error saving currency exchange rate'
    )
  }
}

export const deactivateCurrencyExchangeRate = async (id: number): Promise<CurrencyExchangeRate> => {
  try {
    const response = await axios.post<CurrencyExchangeRate>(
      `${CURRENCY_EXCHANGE_RATES_URL}/${id}/deactivate`
    )
    return response.data
  } catch (error: any) {
    console.error('Error deactivating currency exchange rate:', error)
    throw new Error(error.response?.data?.message || 'Error deactivating currency exchange rate')
  }
}

export const resolveCurrencyExchangeRate = async (
  params: CurrencyExchangeRateResolveRequest
): Promise<CurrencyConversionPreviewResult> => {
  try {
    const response = await axios.get<CurrencyConversionPreviewResult>(
      `${CURRENCY_EXCHANGE_RATES_URL}/resolve`,
      {params}
    )
    return response.data
  } catch (error: any) {
    console.error('Error resolving currency exchange rate:', error)
    throw new Error(error.response?.data?.message || 'Error resolving currency exchange rate')
  }
}

export const previewCurrencySnapshotBackfill = async (
  rateId: number,
  params?: Pick<
    CurrencySnapshotBackfillRequest,
    'includeDraft' | 'includeConfirmedWithMissingSnapshot'
  >
): Promise<CurrencySnapshotBackfillPreview> => {
  try {
    const response = await axios.get<CurrencySnapshotBackfillPreview>(
      `${CURRENCY_EXCHANGE_RATES_URL}/${rateId}/backfill-preview`,
      {params}
    )
    return response.data
  } catch (error: any) {
    console.error('Error previewing currency snapshot backfill:', error)
    throw new Error(error.response?.data?.message || 'Error previewing currency snapshot backfill')
  }
}

export const backfillCurrencySnapshots = async (
  rateId: number,
  payload?: CurrencySnapshotBackfillRequest
): Promise<CurrencySnapshotBackfillResult> => {
  try {
    const response = await axios.post<CurrencySnapshotBackfillResult>(
      `${CURRENCY_EXCHANGE_RATES_URL}/${rateId}/backfill`,
      payload || {}
    )
    return response.data
  } catch (error: any) {
    console.error('Error backfilling currency snapshots:', error)
    throw new Error(error.response?.data?.message || 'Error backfilling currency snapshots')
  }
}

export const previewCurrencyConversion = async (
  payload: CurrencyConversionPreviewRequest
): Promise<CurrencyConversionPreviewResult> => {
  try {
    const response = await axios.post<CurrencyConversionPreviewResult>(
      `${API_URL}/currencies/conversion-preview`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error previewing currency conversion:', error)
    throw new Error(error.response?.data?.message || 'Error previewing currency conversion')
  }
}

export const actOnApprovalInstance = async (
  id: number,
  actionType: ApprovalInstanceActionType,
  remarks?: string
): Promise<ApprovalInstance> => {
  try {
    const response = await axios.post<ApprovalInstance>(`${APPROVAL_INSTANCES_URL}/${id}/actions`, {
      actionType,
      remarks,
    })
    return response.data
  } catch (error: any) {
    console.error('Error acting on approval instance:', error)
    throw new Error(error.response?.data?.message || 'Error acting on approval instance')
  }
}

// Inventory Item Sub Categories
export const getInventoryItemSubCategories = async (
  activeOnly = false
): Promise<InventoryItemSubCategory[]> => {
  try {
    const url = `${INVENTORY_ITEM_SUBCATEGORIES_URL}?activeOnly=${activeOnly}`
    const response = await axios.get<InventoryItemSubCategory[]>(url)
    return response.data
  } catch (error: any) {
    console.error('Error fetching inventory item sub categories:', error)
    throw new Error(error.response?.data?.message || 'Error fetching inventory item sub categories')
  }
}

export const updateRequisitionLineQuantity = async (
  requisitionId: number,
  lineId: number,
  quantity: number,
  remarks?: string
): Promise<Requisition> => {
  try {
    const params: any = {quantity}
    if (remarks && remarks.trim() !== '') params.remarks = remarks.trim()
    const response = await axios.patch<Requisition>(
      `${REQUISITIONS_URL}/${requisitionId}/lines/${lineId}/quantity`,
      null,
      {params}
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating requisition line quantity:', error)
    throw new Error(error.response?.data?.message || 'Error updating requisition line quantity')
  }
}

export const createInventoryItemSubCategory = async (payload: {
  name: string
  description?: string
  active?: boolean
}): Promise<InventoryItemSubCategory> => {
  try {
    const response = await axios.post<InventoryItemSubCategory>(
      INVENTORY_ITEM_SUBCATEGORIES_URL,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating inventory item sub category:', error)
    throw new Error(error.response?.data?.message || 'Error creating inventory item sub category')
  }
}

export const updateInventoryItemSubCategory = async (
  id: number,
  payload: {name?: string; description?: string; active?: boolean}
): Promise<InventoryItemSubCategory> => {
  try {
    const response = await axios.put<InventoryItemSubCategory>(
      `${INVENTORY_ITEM_SUBCATEGORIES_URL}/${id}`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating inventory item sub category:', error)
    throw new Error(error.response?.data?.message || 'Error updating inventory item sub category')
  }
}

export const deleteInventoryItemSubCategory = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${INVENTORY_ITEM_SUBCATEGORIES_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting inventory item sub category:', error)
    throw procurementErr(error, 'Error deleting inventory item sub category')
  }
}

export const createInventoryItemSubHead = async (
  payload: CreateInventoryItemSubHeadRequest
): Promise<InventoryItemSubHead> => {
  try {
    const response = await axios.post<InventoryItemSubHead>(INVENTORY_ITEM_SUBHEADS_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating inventory item sub head:', error)
    throw new Error(error.response?.data?.message || 'Error creating inventory item sub head')
  }
}

export const updateInventoryItemSubHead = async (
  id: number,
  payload: CreateInventoryItemSubHeadRequest
): Promise<InventoryItemSubHead> => {
  try {
    const response = await axios.put<InventoryItemSubHead>(
      `${INVENTORY_ITEM_SUBHEADS_URL}/${id}`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating inventory item sub head:', error)
    throw new Error(error.response?.data?.message || 'Error updating inventory item sub head')
  }
}

export const deleteInventoryItemSubHead = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${INVENTORY_ITEM_SUBHEADS_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting inventory item sub head:', error)
    throw procurementErr(error, 'Error deleting inventory item sub head')
  }
}

// ——— ACCOUNTING ACCOUNTS ——————————————————————————————————————————————————

export const getAccountingAccounts = async (
  cgaId?: number,
  vesselId?: number,
  vesselType?: string
): Promise<AccountingAccount[]> => {
  try {
    // Build query parameters if provided
    const params = new URLSearchParams()
    if (cgaId) params.append('cgaId', cgaId.toString())
    if (vesselId) params.append('vesselId', vesselId.toString())
    if (vesselType) params.append('vesselType', vesselType)

    const url = params.toString() ? `${ACCOUNTING_URL}?${params.toString()}` : ACCOUNTING_URL
    const response = await axios.get<AccountingAccount[]>(url)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching accounting accounts:', error)
    throw new Error(error.response?.data?.message || 'Error fetching accounting accounts')
  }
}

// Function to get all accounting accounts without filtering
export const getAllAccountingAccounts = async (): Promise<AccountingAccount[]> => {
  try {
    const response = await axios.get<AccountingAccount[]>(ACCOUNTING_URL)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching all accounting accounts:', error)
    throw new Error(error.response?.data?.message || 'Error fetching all accounting accounts')
  }
}

export const createAccountingAccount = async (payload: {
  accountCodes: string
  cgaid: {id: number}
  vesselId?: number
  vesselType?: string
  category?: {id: number}
}): Promise<AccountingAccount> => {
  try {
    // Try different payload formats based on the API specification
    // First attempt: exactly as specified in the original API doc
    const apiPayload = {
      accountCodes: payload.accountCodes,
      cgaid: payload.cgaid,
      ...(payload.vesselId ? {vesselId: payload.vesselId} : {}),
      ...(payload.vesselType ? {vesselType: payload.vesselType} : {}),
      ...(payload.category && {category: payload.category}),
    }

    console.log(
      'Creating accounting account with payload (attempt 1 - string vesselType):',
      apiPayload
    )
    const response = await axios.post<AccountingAccount>(ACCOUNTING_URL, apiPayload)
    console.log('Accounting account created:', response.data)
    return response.data
  } catch (error: any) {
    console.error('First attempt failed, trying with object vesselType format')

    try {
      // Second attempt: with vesselType as object
      const apiPayload2 = {
        accountCodes: payload.accountCodes,
        cgaid: payload.cgaid,
        ...(payload.vesselId ? {vesselId: payload.vesselId} : {}),
        ...(payload.vesselType ? {vesselType: {vesselType: payload.vesselType}} : {}),
        ...(payload.category && {category: payload.category}),
      }

      console.log(
        'Creating accounting account with payload (attempt 2 - object vesselType):',
        apiPayload2
      )
      const response = await axios.post<AccountingAccount>(ACCOUNTING_URL, apiPayload2)
      console.log('Accounting account created:', response.data)
      return response.data
    } catch (error2: any) {
      console.error('Both attempts failed')
      console.error('Error creating accounting account:', error2)
      console.error('Full error response:', error2.response)
      throw new Error(error2.response?.data?.message || 'Error creating accounting account')
    }
  }
}

export const updateAccountingAccount = async (
  id: number,
  payload: {
    accountCodes: string
    vesselId?: number
    vesselType?: string
  }
): Promise<AccountingAccount> => {
  try {
    const response = await axios.put<AccountingAccount>(`${ACCOUNTING_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating accounting account:', error)
    throw new Error(error.response?.data?.message || 'Error updating accounting account')
  }
}

// ——— SUB ACCOUNTS ——————————————————————————————————————————————————————————

export const getSubAccounts = async (
  accountId: number,
  cgaId?: number,
  vesselId?: number,
  vesselType?: string
): Promise<SubAccount[]> => {
  try {
    // Build query parameters
    const params = new URLSearchParams()
    params.append('accountId', accountId.toString())
    if (cgaId) params.append('cgaId', cgaId.toString())
    if (vesselId) params.append('vesselId', vesselId.toString())
    if (vesselType) params.append('vesselType', vesselType)

    const response = await axios.get<SubAccount[]>(`${SUBACCOUNTS_URL}?${params.toString()}`)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching sub accounts:', error)
    throw new Error(error.response?.data?.message || 'Error fetching sub accounts')
  }
}

export const createSubAccount = async (payload: {
  subaccountCodes: string
  accountCodeId: number
  cgaid: {id: number}
  vesselId?: number
  vesselType?: string
}): Promise<SubAccount[]> => {
  try {
    const response = await axios.post<SubAccount[]>(SUBACCOUNTS_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating sub account:', error)
    throw new Error(error.response?.data?.message || 'Error creating sub account')
  }
}

export const updateSubAccount = async (
  id: number,
  payload: {
    subaccountCodes: string
    accountCodeId: number
    vesselId?: number
    vesselType?: string
  }
): Promise<SubAccount> => {
  try {
    const response = await axios.put<SubAccount>(`${SUBACCOUNTS_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating sub account:', error)
    throw new Error(error.response?.data?.message || 'Error updating sub account')
  }
}

export const getAllSubAccounts = async (cgaId?: number): Promise<SubAccount[]> => {
  try {
    const params = new URLSearchParams()
    if (cgaId) params.append('cgaId', cgaId.toString())

    const response = await axios.get<SubAccount[]>(
      `${SUBACCOUNTS_URL}${params.toString() ? '?' + params.toString() : ''}`
    )
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching all sub accounts:', error)
    // Keep requisition lookups usable even if sub-account listing has legacy API issues.
    return []
  }
}

// ——— SUB CATALOGUES ——————————————————————————————————————————————————————————

export const getSubCatalogues = async (
  vesselId: number,
  subaccountId: number,
  vesselType?: string
): Promise<SubCatalogue[]> => {
  try {
    const params = new URLSearchParams()
    params.append('subaccountId', subaccountId.toString())
    if (vesselId) params.append('vesselId', vesselId.toString())
    if (vesselType) params.append('vesselType', vesselType)
    const response = await axios.get(`${API_URL}/master-asset/subcats?${params.toString()}`)

    const data = response.data
    if (!data) {
      return []
    }
    return Array.isArray(data) ? data : [data]
  } catch (error: any) {
    console.error('Error fetching sub catalogues:', error)
    // Return empty array instead of throwing error if no data found
    if (error.response?.status === 404) {
      return []
    }
    throw new Error(error.response?.data?.message || 'Error fetching sub catalogues')
  }
}

export const getSubCataloguesBySubAccount = async (
  subaccountId: number,
  vesselId?: number,
  vesselType?: string
): Promise<SubCatalogue[]> => {
  try {
    const params = new URLSearchParams()
    params.append('subaccountId', subaccountId.toString())
    if (vesselId) {
      params.append('vesselId', vesselId.toString())
    }
    if (vesselType) {
      params.append('vesselType', vesselType)
    }

    const response = await axios.get(`${API_URL}/master-asset/subcats?${params.toString()}`)
    const data = response.data
    if (!data) {
      return []
    }
    return Array.isArray(data) ? data : [data]
  } catch (error: any) {
    console.error('Error fetching sub catalogues by sub account:', error)
    if (error.response?.status === 404) {
      return []
    }
    throw new Error(error.response?.data?.message || 'Error fetching sub catalogues')
  }
}

// Cache for sub catalogues - stores data for 5 minutes
let subCataloguesCache: {data: SubCatalogue[]; timestamp: number} | null = null
const CACHE_DURATION_MS = 5 * 60 * 1000 // 5 minutes

// Clear cache when sub catalogues are modified
const clearSubCataloguesCache = () => {
  subCataloguesCache = null
  console.log('🗑️ Sub catalogues cache cleared')
}

export const createSubCatalogue = async (payload: {
  name: string
  vesselId?: number
  vesselType?: string
  cgaid: {id: number}
  subaccounts: Array<{id: number}>
}): Promise<SubCatalogue> => {
  try {
    console.log('createSubCatalogue - Sending payload:', payload)
    const response = await axios.post<SubCatalogue>(SUBCATALOGUES_URL, payload)
    console.log('createSubCatalogue - Response:', response.data)

    // Clear cache so next fetch gets fresh data
    clearSubCataloguesCache()

    return response.data
  } catch (error: any) {
    console.error('Error creating sub catalogue:', error)
    throw new Error(error.response?.data?.message || 'Error creating sub catalogue')
  }
}

export const updateSubCatalogue = async (
  id: number,
  payload: {
    name: string
    vesselId?: number
    vesselType?: string
    cgaid: {id: number}
    subaccounts: Array<{id: number}>
  }
): Promise<SubCatalogue> => {
  try {
    console.log('updateSubCatalogue - Sending payload:', payload)
    const response = await axios.put<SubCatalogue>(`${SUBCATALOGUES_URL}/${id}`, payload)
    console.log('updateSubCatalogue - Response:', response.data)

    // Clear cache so next fetch gets fresh data
    clearSubCataloguesCache()

    return response.data
  } catch (error: any) {
    console.error('Error updating sub catalogue:', error)
    throw new Error(error.response?.data?.message || 'Error updating sub catalogue')
  }
}

// Simple function to get all sub catalogues without parameters (for superadmin)
export const getAllSubCatalogues = async (): Promise<SubCatalogue[]> => {
  try {
    console.log('getAllSubCatalogues - Fetching from /subcats without parameters...')
    const response = await axios.get(`${API_URL}/master-asset/subcats`)
    console.log('getAllSubCatalogues - Response:', response.data)

    const data = response.data
    if (!data) {
      return []
    }
    return Array.isArray(data) ? data : [data]
  } catch (error: any) {
    console.error('Error fetching all sub catalogues:', error)
    if (error.response?.status === 404) {
      return []
    }
    throw new Error(error.response?.data?.message || 'Error fetching all sub catalogues')
  }
}

// ——— PARTS BY SUB CATALOGUE ——————————————————————————————————————————————————

export const getPartsBySubCatalogue = async (subcatalogueId: number): Promise<Part[]> => {
  try {
    const response = await axios.get<Part[]>(`${PARTS_URL}/by-subcat/${subcatalogueId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching parts by sub catalogue:', error)
    throw new Error(error.response?.data?.message || 'Error fetching parts by sub catalogue')
  }
}

export const getPartsBySubcomponent = async (subcomponentId: number): Promise<Part[]> => {
  try {
    const response = await axios.get<Part[]>(`${PARTS_URL}/by-subcomponent/${subcomponentId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching parts by subcomponent:', error)
    throw new Error(error.response?.data?.message || 'Error fetching parts by subcomponent')
  }
}

// ——— SUB COMPONENTS ——————————————————————————————————————————————————————————

export const getSubComponents = async (): Promise<SubComponent[]> => {
  try {
    const response = await axios.get<SubComponent[]>(SUBCOMPONENTS_URL)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching sub components:', error)
    throw new Error(error.response?.data?.message || 'Error fetching sub components')
  }
}

export const getSubComponentsByPartId = async (partId: number): Promise<SubComponent[]> => {
  try {
    const url = `${SUBCOMPONENTS_URL}?partId=${partId}`
    console.log('Fetching subcomponents from URL:', url)
    const response = await axios.get<SubComponent[]>(url)
    console.log('Raw API response:', response)
    console.log('Response data:', response.data)
    console.log('Is array?', Array.isArray(response.data))

    const result = Array.isArray(response.data) ? response.data : [response.data]
    console.log('Returning subcomponents:', result)
    return result
  } catch (error: any) {
    console.error('Error fetching sub components by part:', error)
    console.error('Error details:', error.response?.data)
    throw new Error(error.response?.data?.message || 'Error fetching sub components by part')
  }
}

export const createSubComponent = async (
  payload: CreateSubComponentRequest
): Promise<SubComponent> => {
  try {
    const response = await axios.post<SubComponent>(SUBCOMPONENTS_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating sub component:', error)
    throw new Error(error.response?.data?.message || 'Error creating sub component')
  }
}

export const updateSubComponent = async (
  id: number,
  payload: CreateSubComponentRequest
): Promise<SubComponent> => {
  try {
    const response = await axios.put<SubComponent>(`${SUBCOMPONENTS_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating sub component:', error)
    throw new Error(error.response?.data?.message || 'Error updating sub component')
  }
}

export const deleteSubComponent = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${SUBCOMPONENTS_URL}/${id}`)
  } catch (error: any) {
    console.error('Error deleting sub component:', error)
    throw new Error(error.response?.data?.message || 'Error deleting sub component')
  }
}

// ——— EQUIPMENT ————————————————————————————————————————————————————————————

export const getEquipmentList = async (vesselId?: number, q?: string): Promise<Equipment[]> => {
  const params: Record<string, unknown> = {}
  if (vesselId) params.vesselId = vesselId
  if (q) params.q = q
  const res = await axios.get(EQUIPMENT_URL, {params})
  return normalizeEquipmentRecords(res.data)
}

export const getRequisitionFormEquipment = async (
  vesselId?: number,
  q?: string
): Promise<RequisitionFormEquipmentLookup[]> => {
  const params: Record<string, unknown> = {}
  if (vesselId) params.vesselId = vesselId
  if (q) params.q = q
  const res = await axios.get<RequisitionFormEquipmentLookup[]>(`${EQUIPMENT_URL}/requisition-form`, {
    params,
  })
  return Array.isArray(res.data) ? res.data : []
}

export const createEquipment = async (payload: Partial<Equipment>): Promise<Equipment> => {
  const res = await axios.post<Equipment>(EQUIPMENT_URL, payload)
  return res.data
}

export const updateEquipment = async (
  id: number,
  payload: Partial<Equipment>
): Promise<Equipment> => {
  const res = await axios.put<Equipment>(`${EQUIPMENT_URL}/${id}`, payload)
  return res.data
}

export const deleteEquipment = async (id: number): Promise<void> => {
  await axios.delete(`${EQUIPMENT_URL}/${id}`)
}

// ——— EQUIPMENT COMPONENTS ————————————————————————————————————————————————

export const getEquipmentComponents = async (
  equipmentId?: number,
  q?: string
): Promise<EquipmentComponent[]> => {
  const params: any = {}
  if (equipmentId) params.equipmentId = equipmentId
  if (q) params.q = q
  const res = await axios.get<EquipmentComponent[]>(EQUIPMENT_COMPONENTS_URL, {params})
  return res.data
}

export const getEquipmentComponentsByEquipment = async (
  equipmentId: number
): Promise<EquipmentComponent[]> => {
  const res = await axios.get<EquipmentComponent[]>(
    `${EQUIPMENT_COMPONENTS_URL}/by-equipment/${equipmentId}`
  )
  return res.data
}

export const createEquipmentComponent = async (
  payload: Partial<EquipmentComponent>
): Promise<EquipmentComponent> => {
  const res = await axios.post<EquipmentComponent>(EQUIPMENT_COMPONENTS_URL, payload)
  return res.data
}

export const updateEquipmentComponent = async (
  id: number,
  payload: Partial<EquipmentComponent>
): Promise<EquipmentComponent> => {
  const res = await axios.put<EquipmentComponent>(`${EQUIPMENT_COMPONENTS_URL}/${id}`, payload)
  return res.data
}

export const deleteEquipmentComponent = async (id: number): Promise<void> => {
  await axios.delete(`${EQUIPMENT_COMPONENTS_URL}/${id}`)
}

// Subcomponents by equipment component
export const getSubComponentsByEquipmentComponent = async (
  equipmentComponentId: number
): Promise<SubComponent[]> => {
  const res = await axios.get<SubComponent[]>(
    `${SUBCOMPONENTS_URL}?equipmentComponentId=${equipmentComponentId}&includeInstances=true`
  )
  return Array.isArray(res.data) ? res.data : [res.data]
}

// Subcomponents by equipment
export const getSubComponentsByEquipment = async (
  equipmentId: number,
  includeInstances: boolean = true
): Promise<SubComponent[]> => {
  const res = await axios.get<SubComponent[]>(SUBCOMPONENTS_URL, {
    params: {equipmentId, includeInstances},
  })
  return Array.isArray(res.data) ? res.data : [res.data]
}

// ——— DELETE FUNCTIONS ——————————————————————————————————————————————————————————

export const deleteAccountingAccount = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${ACCOUNTING_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting accounting account:', error)
    throw procurementErr(error, 'Error deleting accounting account')
  }
}

export const deleteSubAccount = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${SUBACCOUNTS_URL}/${id}`)
  } catch (error: unknown) {
    console.error('Error deleting sub account:', error)
    throw procurementErr(error, 'Error deleting sub account')
  }
}

export const deleteSubCatalogue = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${SUBCATALOGUES_URL}/${id}`)
  } catch (error: any) {
    console.error('Error deleting sub catalogue:', error)
    throw new Error(error.response?.data?.message || 'Error deleting sub catalogue')
  }
}

// ——— COMPANY ADMINS ——————————————————————————————————————————————————————————

export const getCompanyAdminList = async (): Promise<CompanyAdmin[]> => {
  try {
    const response = await axios.get<CompanyAdmin[]>(`${API_URL}/company-group-admins`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching company admin list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company admin list')
  }
}

// ——— COMPANY GROUP ADMINS (for superadmin dropdowns) ——————————————————————————

export const getCompanyGroupAdmins = async (): Promise<CompanyGroupAdmin[]> => {
  try {
    const response = await axios.get<CompanyGroupAdmin[]>(`${API_URL}/company-group-admins`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching company group admins:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company group admins')
  }
}

export const getRequisitionFormCompanies = async (): Promise<RequisitionFormCompanyLookup[]> => {
  try {
    const response = await axios.get<RequisitionFormCompanyLookup[]>(
      `${API_URL}/company-group-admins/requisition-form`
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching requisition form companies:', error)
    throw new Error(error.response?.data?.message || 'Error fetching requisition form companies')
  }
}

// ——— SUB COMPANY ADMINS (for subcompany dropdowns) ——————————————————————————

export const getSubCompanyAdmins = async (
  companyGroupAdminId: number
): Promise<SubCompanyAdmin[]> => {
  try {
    const response = await axios.get<SubCompanyAdmin[]>(`${API_URL}/company-admins`, {
      params: {companyGroupAdminId},
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching sub company admins:', error)
    throw new Error(error.response?.data?.message || 'Error fetching sub company admins')
  }
}

// ——— VESSELS BY COMPANY (for vessel type dropdowns) ——————————————————————————

export const getVesselsByCompany = async (companyUsername: string): Promise<Vessel[]> => {
  try {
    const response = await axios.get<any>(`${API_URL}/vessels/vessels-by-cga`, {
      params: {companyUsername},
    })
    const data = response.data
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.content)) return data.content
    return []
  } catch (error: any) {
    console.error('Error fetching vessels by company:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessels by company')
  }
}

export const getRequisitionFormVessels = async (
  companyUsername?: string
): Promise<RequisitionFormVesselLookup[]> => {
  try {
    const response = await axios.get<RequisitionFormVesselLookup[]>(
      `${API_URL}/vessels/requisition-form`,
      {
        params: companyUsername ? {companyUsername} : undefined,
      }
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching requisition form vessels:', error)
    throw new Error(error.response?.data?.message || 'Error fetching requisition form vessels')
  }
}

export const getVesselById = async (vesselId: number): Promise<Vessel> => {
  try {
    const response = await axios.get<Vessel>(`${API_URL}/vessels/${vesselId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching vessel by ID:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel by ID')
  }
}

export const getCompanyPoInfo = async (companyGroupAdminId?: number): Promise<CompanyPoInfo> => {
  try {
    const params: any = {}
    if (companyGroupAdminId && companyGroupAdminId > 0) {
      params.companyGroupAdminId = companyGroupAdminId
    }
    const response = await axios.get<CompanyPoInfo>(COMPANY_PO_INFO_URL, {params})
    return response.data || {}
  } catch (error: any) {
    console.error('Error fetching company PO info:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company PO info')
  }
}

export const saveCompanyPoInfo = async (
  payload: Partial<CompanyPoInfo>,
  companyGroupAdminId?: number
): Promise<CompanyPoInfo> => {
  try {
    const params: any = {}
    if (companyGroupAdminId && companyGroupAdminId > 0) {
      params.companyGroupAdminId = companyGroupAdminId
    }
    const response = await axios.put<CompanyPoInfo>(COMPANY_PO_INFO_URL, payload || {}, {params})
    return response.data
  } catch (error: any) {
    console.error('Error saving company PO info:', error)
    throw new Error(error.response?.data?.message || 'Error saving company PO info')
  }
}

export const getAllVessels = async (): Promise<Vessel[]> => {
  try {
    const response = await axios.get<Vessel[]>(`${API_URL}/vessels`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching all vessels:', error)
    throw new Error(error.response?.data?.message || 'Error fetching all vessels')
  }
}

// ——— NEW INVENTORY MANAGEMENT APIs ——————————————————————————————————————————

// Updated Accounting Accounts API with cgaId and vesselType parameters
export const getAccountingAccountsWithParams = async (
  cgaId: number,
  vesselId?: number,
  vesselType?: string
): Promise<AccountingAccount[]> => {
  try {
    const params = new URLSearchParams()
    params.append('cgaId', cgaId.toString())
    if (vesselId) params.append('vesselId', vesselId.toString())
    if (vesselType) params.append('vesselType', vesselType)
    const response = await axios.get<AccountingAccount[]>(`${ACCOUNTING_URL}?${params.toString()}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching accounting accounts with params:', error)
    throw new Error(error.response?.data?.message || 'Error fetching accounting accounts')
  }
}

// ——— PURCHASE ORDERS ————————————————————————————————————————————————————————

const PURCHASE_ORDERS_URL = `${API_URL}/pos`

export const getPurchaseOrdersPage = async (
  page: number = 0,
  size: number = 10,
  filters?: {companyId?: number; companyType?: string; vesselId?: number}
): Promise<PurchaseOrderListPageResponse> => {
  try {
    const response = await axios.get<any>(PURCHASE_ORDERS_URL, {
      params: {
        page,
        size,
        ...(filters?.companyId ? {companyId: filters.companyId} : {}),
        ...(filters?.companyType ? {companyType: filters.companyType} : {}),
        ...(filters?.vesselId ? {vesselId: filters.vesselId} : {}),
      },
    })
    return normalizePagedResponse<any>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching purchase orders:', error)
    throw new Error(error.response?.data?.message || 'Error fetching purchase orders')
  }
}

export const approveVendorLogisticsChange = async (poId: number, remark?: string): Promise<any> => {
  try {
    const response = await axios.post(`${PURCHASE_ORDERS_URL}/${poId}/vendor-logistics-change/accept`, {
      remark: remark || undefined,
    })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Unable to approve vendor logistics change')
  }
}

export const rejectVendorLogisticsChange = async (poId: number, remark: string): Promise<any> => {
  try {
    const response = await axios.post(`${PURCHASE_ORDERS_URL}/${poId}/vendor-logistics-change/reject`, {
      remark,
    })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Unable to reject vendor logistics change')
  }
}

export const ownerApproveQuotation = async (quotationId: number, remark?: string): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/owner-approve`, {remark})
    return response.data
  } catch (error: any) {
    console.error('Error approving quotation by owner:', error)
    throw new Error(error.response?.data?.message || 'Error approving quotation')
  }
}

export const ownerRejectQuotation = async (quotationId: number, remark: string): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/owner-reject`, {remark})
    return response.data
  } catch (error: any) {
    console.error('Error rejecting quotation by owner:', error)
    throw new Error(error.response?.data?.message || 'Error rejecting quotation')
  }
}

export const sendQuotationToVessel = async (quotationId: number, remark?: string): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/send-to-vessel`, {remark})
    return response.data
  } catch (error: any) {
    console.error('Error sending quotation to vessel:', error)
    throw new Error(error.response?.data?.message || 'Error sending quotation to vessel')
  }
}

export const sendQuotationsToVesselSelection = async (
  rfqId: number,
  remark?: string
): Promise<any[]> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/rfq/${rfqId}/send-to-vessel-selection`, {
      remark,
    })
    return response.data
  } catch (error: any) {
    console.error('Error sending quotations to vessel for selection:', error)
    throw new Error(
      error.response?.data?.message || 'Error sending quotations to vessel for selection'
    )
  }
}

export const vesselSelectQuotation = async (quotationId: number, remark?: string): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/vessel-select`, {remark})
    return response.data
  } catch (error: any) {
    console.error('Error marking quotation as vessel selected:', error)
    throw new Error(error.response?.data?.message || 'Error marking quotation as vessel selected')
  }
}

export const vesselRejectQuotation = async (quotationId: number, remark?: string): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/vessel-reject`, {remark})
    return response.data
  } catch (error: any) {
    console.error('Error marking quotation as vessel rejected:', error)
    throw new Error(error.response?.data?.message || 'Error marking quotation as vessel rejected')
  }
}

export const requestQuotationRework = async (quotationId: number, remark: string): Promise<any> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/${quotationId}/rework`, {remark})
    return response.data
  } catch (error: any) {
    console.error('Error requesting quotation rework:', error)
    throw new Error(error.response?.data?.message || 'Error requesting quotation rework')
  }
}

export const requestQuotationReworkForRfq = async (
  quotationIds: number[],
  remarks: string
): Promise<any[]> => {
  try {
    const response = await axios.post(`${QUOTATIONS_URL}/rework`, {quotationIds, remarks})
    return response.data || []
  } catch (error: any) {
    console.error('Error requesting quotation rework:', error)
    throw new Error(error.response?.data?.message || 'Error requesting quotation rework')
  }
}

export const getPurchaseOrders = async (): Promise<PurchaseOrder[]> => {
  const page = await getPurchaseOrdersPage(0, 1000)
  return page.content || []
}

export const getPurchaseOrderById = async (poId: number): Promise<PurchaseOrder> => {
  try {
    const response = await axios.get<PurchaseOrder>(`${PURCHASE_ORDERS_URL}/${poId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching purchase order:', error)
    throw procurementErr(error, 'Error fetching purchase order')
  }
}

export const getPurchaseOrderDocumentById = async (poId: number): Promise<PurchaseOrder> => {
  try {
    const response = await axios.get<PurchaseOrder>(`${PURCHASE_ORDERS_URL}/${poId}/document`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching purchase order document:', error)
    throw procurementErr(error, 'Error fetching purchase order document')
  }
}

export const getPurchaseOrdersFilteredPage = async (
  companyUsername?: string,
  vesselId?: number,
  page: number = 0,
  size: number = 10
): Promise<PurchaseOrderPageResponse> => {
  try {
    const params: any = {}
    if (companyUsername) params.companyUsername = companyUsername
    if (vesselId) params.vesselId = vesselId
    params.page = page
    params.size = size
    const response = await axios.get<any>(`${PURCHASE_ORDERS_URL}/filter`, {params})
    return normalizePagedResponse<PurchaseOrder>(response.data, page, size)
  } catch (error: any) {
    console.error('Error filtering purchase orders:', error)
    throw new Error(error.response?.data?.message || 'Error filtering purchase orders')
  }
}

export const getPurchaseOrdersFiltered = async (
  companyUsername?: string,
  vesselId?: number
): Promise<PurchaseOrder[]> => {
  const page = await getPurchaseOrdersFilteredPage(companyUsername, vesselId, 0, 1000)
  return page.content || []
}

export const sendPurchaseOrder = async (poId: number): Promise<any> => {
  try {
    const response = await axios.post<any>(`${PURCHASE_ORDERS_URL}/${poId}/send`)
    return response.data
  } catch (error: any) {
    console.error('Error sending purchase order:', error)
    throw new Error(error.response?.data?.message || 'Error sending purchase order')
  }
}

export const updateCompanyPurchaseOrderLines = async (
  poId: number,
  quotationLines: Array<{
    requisitionLineId?: number | null
    inventoryItemId?: number | null
    masterInventoryItemId?: number | null
    partId?: number | null
    unitOfMeasurement?: string | null
    manualItemName?: string | null
    quantity: number
    unitPrice?: number | null
  }>
): Promise<PurchaseOrder> => {
  try {
    const response = await axios.put<PurchaseOrder>(`${PURCHASE_ORDERS_URL}/${poId}`, {
      quotationLines,
    })
    return response.data
  } catch (error: any) {
    console.error('Error updating company purchase order lines:', error)
    throw new Error(error.response?.data?.message || 'Error updating purchase order lines')
  }
}

export const createPurchaseOrderFromQuotation = async (
  quotationId: number,
  payload?: {
    poDate?: string | null
    supplierReference?: string | null
    logisticAccountCode?: string | null
    logisticSubaccountCode?: string | null
    invoiceAddressedTo?: string | null
    billingAddressDisplay?: string | null
    poRaisedByCompany?: string | null
    poRaisedByUser?: string | null
    accountCategory?: string | null
    budgetCode?: string | null
    ownerApprovalRequired?: boolean
    ownerApprovalRemark?: string | null
    deliveryDate?: string | null
    deliveryTerms?: string | null
    terms?: string | null
    paymentTerms?: string | null
    specialInstructions?: string | null
  }
): Promise<PurchaseOrderMutationResponse> => {
  try {
    const response = await axios.post<PurchaseOrderMutationResponse>(
      `${PURCHASE_ORDERS_URL}/from-quotation/${quotationId}`,
      payload || {}
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating purchase order from quotation:', error)
    throw new Error(error.response?.data?.message || 'Error creating purchase order from quotation')
  }
}

export type DirectPurchaseOrderFiles = {
  requisitionFiles?: File[]
  rfqFiles?: File[]
  quotationFiles?: File[]
  poFiles?: File[]
  lineFilesByIndex?: Record<number, File[]>
}

const appendIfPresent = (
  formData: FormData,
  key: string,
  value: string | number | boolean | null | undefined
) => {
  if (value === null || value === undefined || value === '') {
    return
  }
  formData.append(key, String(value))
}

export const buildDirectPurchaseOrderFormData = (
  payload: DirectPurchaseOrderRequest,
  files?: DirectPurchaseOrderFiles
) => {
  const formData = new FormData()

  appendIfPresent(formData, 'title', payload.title)
  appendIfPresent(formData, 'description', payload.description)
  appendIfPresent(formData, 'requisitionDescription', payload.requisitionDescription)
  appendIfPresent(formData, 'rfqTitle', payload.rfqTitle)
  appendIfPresent(formData, 'rfqItemDescription', payload.rfqItemDescription)
  appendIfPresent(formData, 'technicalSpecifications', payload.technicalSpecifications)
  appendIfPresent(formData, 'requisitionType', payload.requisitionType)
  appendIfPresent(formData, 'requestFor', payload.requestFor)
  appendIfPresent(formData, 'vesselId', payload.vesselId)
  appendIfPresent(formData, 'categoryId', payload.categoryId)
  appendIfPresent(formData, 'requestedPortId', payload.requestedPortId)
  appendIfPresent(formData, 'deliveryPortId', payload.deliveryPortId)
  appendIfPresent(formData, 'requiredDeliveryDate', payload.requiredDeliveryDate)
  appendIfPresent(formData, 'responseDueDate', payload.responseDueDate)
  appendIfPresent(formData, 'promisedDeliveryDate', payload.promisedDeliveryDate)
  appendIfPresent(formData, 'vendorId', payload.vendorId)
  appendIfPresent(formData, 'vendorCompanyMappingId', payload.vendorCompanyMappingId)
  appendIfPresent(formData, 'poDate', payload.poDate)
  appendIfPresent(formData, 'supplierReference', payload.supplierReference)
  appendIfPresent(formData, 'logisticAccountCode', payload.logisticAccountCode)
  appendIfPresent(formData, 'invoiceAddressedTo', payload.invoiceAddressedTo)
  appendIfPresent(formData, 'poRaisedByCompany', payload.poRaisedByCompany)
  appendIfPresent(formData, 'poRaisedByUser', payload.poRaisedByUser)
  appendIfPresent(formData, 'accountCategory', payload.accountCategory)
  appendIfPresent(formData, 'deliveryDate', payload.deliveryDate)
  appendIfPresent(formData, 'deliveryTerms', payload.deliveryTerms)
  appendIfPresent(formData, 'paymentTerms', payload.paymentTerms)
  appendIfPresent(formData, 'termsOfPayment', payload.termsOfPayment)
  appendIfPresent(formData, 'terms', payload.terms)
  appendIfPresent(formData, 'specialInstructions', payload.specialInstructions)
  appendIfPresent(formData, 'remittanceInstructions', payload.remittanceInstructions)
  appendIfPresent(formData, 'currency', payload.currency)
  appendIfPresent(formData, 'quotationCurrency', payload.quotationCurrency)
  appendIfPresent(formData, 'companyCurrencyCode', payload.companyCurrencyCode)
  appendIfPresent(formData, 'exchangeRate', payload.exchangeRate)
  appendIfPresent(formData, 'exchangeRateDate', payload.exchangeRateDate)
  appendIfPresent(formData, 'exchangeRateDirect', payload.exchangeRateDirect)
  appendIfPresent(formData, 'totalDiscount', payload.totalDiscount)
  appendIfPresent(formData, 'freightCost', payload.freightCost)
  appendIfPresent(formData, 'packagingCost', payload.packagingCost)
  appendIfPresent(formData, 'truckCost', payload.truckCost)
  appendIfPresent(formData, 'bargeCost', payload.bargeCost)
  appendIfPresent(formData, 'otherCost', payload.otherCost)
  appendIfPresent(formData, 'vatOnLogistics', payload.vatOnLogistics)
  ;(payload.lines || []).forEach((line, index) => {
    appendIfPresent(formData, `lines[${index}].id`, line.id)
    appendIfPresent(formData, `lines[${index}].partId`, line.partId)
    appendIfPresent(formData, `lines[${index}].subcomponentId`, line.subcomponentId)
    appendIfPresent(formData, `lines[${index}].inventoryItemId`, line.inventoryItemId)
    appendIfPresent(formData, `lines[${index}].masterInventoryItemId`, line.masterInventoryItemId)
    appendIfPresent(formData, `lines[${index}].manualItemName`, line.manualItemName)
    appendIfPresent(formData, `lines[${index}].unitOfMeasurement`, line.unitOfMeasurement)
    appendIfPresent(formData, `lines[${index}].quantity`, line.quantity)
    appendIfPresent(formData, `lines[${index}].unitPrice`, line.unitPrice)
    appendIfPresent(formData, `lines[${index}].assignedQuantity`, line.assignedQuantity)
    appendIfPresent(formData, `lines[${index}].notes`, line.notes)
    appendIfPresent(formData, `lines[${index}].logisticsAccountCodeId`, line.logisticsAccountCodeId)
    appendIfPresent(formData, `lines[${index}].logisticsSubaccountCodeId`, line.logisticsSubaccountCodeId)
    appendIfPresent(formData, `lines[${index}].budgetAccountCodeId`, line.budgetAccountCodeId)
    appendIfPresent(formData, `lines[${index}].budgetSubaccountCodeId`, line.budgetSubaccountCodeId)
    appendIfPresent(formData, `lines[${index}].supplyCapacity`, line.supplyCapacity)
    appendIfPresent(formData, `lines[${index}].deliveryTimeDays`, line.deliveryTimeDays)
  })

  files?.requisitionFiles?.forEach((file) => formData.append('requisitionFiles', file))
  files?.rfqFiles?.forEach((file) => formData.append('rfqFiles', file))
  files?.quotationFiles?.forEach((file) => formData.append('quotationFiles', file))
  files?.poFiles?.forEach((file) => formData.append('poFiles', file))
  Object.entries(files?.lineFilesByIndex || {}).forEach(([index, lineFiles]) => {
    lineFiles.forEach((file) => formData.append(`lines[${index}].lineFiles`, file))
  })

  return formData
}

const hasDirectPurchaseOrderFiles = (files?: DirectPurchaseOrderFiles) =>
  Boolean(
    files?.requisitionFiles?.length ||
      files?.rfqFiles?.length ||
      files?.quotationFiles?.length ||
      files?.poFiles?.length ||
      Object.values(files?.lineFilesByIndex || {}).some((entry) => entry.length > 0)
  )

export const createDirectPurchaseOrder = async (
  payload: DirectPurchaseOrderRequest,
  files?: DirectPurchaseOrderFiles
): Promise<PurchaseOrderMutationResponse> => {
  try {
    if (hasDirectPurchaseOrderFiles(files)) {
      const formData = buildDirectPurchaseOrderFormData(payload, files)
      const response = await axios.post<PurchaseOrderMutationResponse>(`${PURCHASE_ORDERS_URL}/direct`, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
      })
      return response.data
    }
    const response = await axios.post<PurchaseOrderMutationResponse>(`${PURCHASE_ORDERS_URL}/direct`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating direct purchase order:', error)
    throw procurementErr(error, 'Error creating direct purchase order')
  }
}

export const previewNextPurchaseOrderNumber = async (params: {
  vesselId: number
  vendorId: number
  poDate?: string
}): Promise<PurchaseOrderNumberPreview> => {
  try {
    const response = await axios.get<PurchaseOrderNumberPreview>(
      `${PURCHASE_ORDERS_URL}/next-number-preview`,
      {params}
    )
    return response.data
  } catch (error: any) {
    throw procurementErr(error, 'Unable to preview purchase-order number')
  }
}

export const updateDirectPurchaseOrder = async (
  poId: number,
  payload: DirectPurchaseOrderRequest,
  files?: DirectPurchaseOrderFiles
): Promise<PurchaseOrderMutationResponse> => {
  try {
    if (hasDirectPurchaseOrderFiles(files)) {
      const formData = buildDirectPurchaseOrderFormData(payload, files)
      const response = await axios.put<PurchaseOrder>(
        `${PURCHASE_ORDERS_URL}/${poId}/direct`,
        formData,
        {
          headers: {'Content-Type': 'multipart/form-data'},
        }
      )
      return response.data
    }
    const response = await axios.put<PurchaseOrder>(
      `${PURCHASE_ORDERS_URL}/${poId}/direct`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating direct purchase order:', error)
    throw procurementErr(error, 'Error updating direct purchase order')
  }
}

export const getOwnerApprovalQueuePage = async (
  page: number = 0,
  size: number = 10
): Promise<PurchaseOrderListPageResponse> => {
  try {
    const response = await axios.get<any>(`${PURCHASE_ORDERS_URL}/owner-approval-queue`, {
      params: {page, size},
    })
    return normalizePagedResponse<any>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching owner approval queue:', error)
    throw new Error(error.response?.data?.message || 'Error fetching owner approval queue')
  }
}

export const ownerApprovePurchaseOrder = async (
  poId: number,
  remark?: string
): Promise<PurchaseOrder> => {
  try {
    const response = await axios.post<PurchaseOrder>(
      `${PURCHASE_ORDERS_URL}/${poId}/owner-approve`,
      {remark}
    )
    return response.data
  } catch (error: any) {
    console.error('Error approving purchase order by owner:', error)
    throw new Error(error.response?.data?.message || 'Error approving purchase order')
  }
}

export const ownerRejectPurchaseOrder = async (
  poId: number,
  remark: string
): Promise<PurchaseOrder> => {
  try {
    const response = await axios.post<PurchaseOrder>(
      `${PURCHASE_ORDERS_URL}/${poId}/owner-reject`,
      {remark}
    )
    return response.data
  } catch (error: any) {
    console.error('Error rejecting purchase order by owner:', error)
    throw new Error(error.response?.data?.message || 'Error rejecting purchase order')
  }
}

export const ownerReworkPurchaseOrder = async (
  poId: number,
  remark: string
): Promise<PurchaseOrder> => {
  try {
    const response = await axios.post<PurchaseOrder>(
      `${PURCHASE_ORDERS_URL}/${poId}/owner-rework`,
      {remark}
    )
    return response.data
  } catch (error: any) {
    console.error('Error requesting purchase order rework by owner:', error)
    throw new Error(error.response?.data?.message || 'Error requesting purchase order rework')
  }
}

// ——— INVOICES ———————————————————————————————————————————————————————————————

export const getInvoicesPage = async (
  page: number = 0,
  size: number = 10,
  filters?: {companyId?: number; companyType?: string; vesselId?: number}
): Promise<InvoicePageResponse> => {
  try {
    console.log('Fetching invoices from:', INVOICES_URL)
    const response = await axios.get<any>(INVOICES_URL, {
      params: {
        page,
        size,
        ...(filters?.companyId ? {companyId: filters.companyId} : {}),
        ...(filters?.companyType ? {companyType: filters.companyType} : {}),
        ...(filters?.vesselId ? {vesselId: filters.vesselId} : {}),
      },
      headers: {
        Accept: 'application/json',
      },
    })
    console.log('Invoices fetched successfully:', response.data)
    return normalizePagedResponse<InvoiceListRow>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching invoices:', error)
    throw procurementErr(error, 'Error fetching invoices')
  }
}

export const getInvoices = async (): Promise<InvoiceListRow[]> => {
  const page = await getInvoicesPage(0, 1000)
  return page.content || []
}

export const getProcurementDashboard = async (filters?: {
  companyId?: number
  companyType?: string
  vesselId?: number
}): Promise<any> => {
  const response = await axios.get(PROCUREMENT_DASHBOARD_URL, {params: filters})
  return response.data
}

export const getInvoiceApprovalSummaries = async (
  ids: number[]
): Promise<InvoiceApprovalSummary[]> => {
  try {
    if (!ids.length) {
      return []
    }
    const response = await axios.get<InvoiceApprovalSummary[]>(`${INVOICES_URL}/approval-summaries`, {
      params: {ids},
      paramsSerializer: (params) => serializeProcurementQueryParams(params as Record<string, unknown>),
    })
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching invoice approval summaries:', error)
    throw new Error(error.response?.data?.message || 'Error fetching invoice approval summaries')
  }
}

export const getEligibleInvoicePurchaseOrders = async (
  companyUsername?: string,
  vesselId?: number
): Promise<InvoiceEligiblePurchaseOrder[]> => {
  try {
    const params: Record<string, string | number> = {}
    if (companyUsername) params.companyUsername = companyUsername
    if (vesselId) params.vesselId = vesselId
    const response = await axios.get<InvoiceEligiblePurchaseOrder[]>(
      `${INVOICES_URL}/eligible-purchase-orders`,
      {
        params,
      }
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching eligible invoice purchase orders:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching eligible invoice purchase orders'
    )
  }
}

export const getEligibleInvoiceGoodsReceipts = async (
  purchaseOrderId: number
): Promise<InvoiceEligibleGoodsReceipt[]> => {
  try {
    const response = await axios.get<InvoiceEligibleGoodsReceipt[]>(
      `${INVOICES_URL}/eligible-goods-receipts`,
      {
        params: {purchaseOrderId},
      }
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching eligible invoice goods receipts:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching eligible invoice goods receipts'
    )
  }
}

export const getInvoiceById = async (invoiceId: number): Promise<InvoiceDetail> => {
  try {
    const response = await axios.get<InvoiceDetail>(`${INVOICES_URL}/${invoiceId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching invoice:', error)
    throw new Error(error.response?.data?.message || 'Error fetching invoice')
  }
}

export const createInvoice = async (payload: CreateInvoiceRequest): Promise<Invoice> => {
  try {
    console.log('Creating invoice with payload:', payload)
    const response = await axios.post<Invoice>(INVOICES_URL, payload, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
    console.log('Invoice created successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error creating invoice:', error)
    throw new Error(error.response?.data?.message || 'Error creating invoice')
  }
}

export const deleteInvoice = async (invoiceId: number): Promise<void> => {
  try {
    await axios.delete(`${INVOICES_URL}/${invoiceId}`)
  } catch (error: any) {
    console.error('Error deleting invoice:', error)
    throw new Error(error.response?.data?.message || 'Error deleting invoice')
  }
}

export const uploadInvoiceAttachment = async (invoiceId: number, file: File): Promise<Invoice> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    const response = await axios.post<Invoice>(
      `${INVOICES_URL}/${invoiceId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error uploading invoice attachment:', error)
    throw new Error(error.response?.data?.message || 'Error uploading invoice attachment')
  }
}

export const markInvoiceAsPaid = async (invoiceId: number): Promise<Invoice> => {
  try {
    console.log('Marking invoice as paid:', invoiceId)
    const response = await axios.post<Invoice>(
      `${INVOICES_URL}/${invoiceId}/paid`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    )
    console.log('Invoice marked as paid successfully:', response.data)
    return response.data
  } catch (error: any) {
    console.error('Error marking invoice as paid:', error)
    throw new Error(error.response?.data?.message || 'Error marking invoice as paid')
  }
}

export const markInvoiceAsPartialPaid = async (
  invoiceId: number,
  amount?: number
): Promise<Invoice> => {
  try {
    console.log('Marking invoice as partially paid:', invoiceId, amount)
    const params: any = {}
    if (amount !== undefined) params.amount = amount
    const response = await axios.post<Invoice>(
      `${INVOICES_URL}/${invoiceId}/partial-paid`,
      {},
      {params}
    )
    return response.data
  } catch (error: any) {
    console.error('Error marking invoice as partially paid:', error)
    throw new Error(error.response?.data?.message || 'Error marking invoice as partially paid')
  }
}

export const approveInvoice = async (invoiceId: number, remark?: string): Promise<Invoice> => {
  try {
    const response = await axios.post<Invoice>(`${INVOICES_URL}/${invoiceId}/approval/approve`, {
      remark,
    })
    return response.data
  } catch (error: any) {
    console.error('Error approving invoice:', error)
    throw new Error(error.response?.data?.message || 'Error approving invoice')
  }
}

export const rejectInvoice = async (invoiceId: number, remark?: string): Promise<Invoice> => {
  try {
    const response = await axios.post<Invoice>(`${INVOICES_URL}/${invoiceId}/approval/reject`, {
      remark,
    })
    return response.data
  } catch (error: any) {
    console.error('Error rejecting invoice:', error)
    throw new Error(error.response?.data?.message || 'Error rejecting invoice')
  }
}

export const reworkInvoice = async (invoiceId: number, remark?: string): Promise<Invoice> => {
  try {
    const response = await axios.post<Invoice>(`${INVOICES_URL}/${invoiceId}/approval/rework`, {
      remark,
    })
    return response.data
  } catch (error: any) {
    console.error('Error sending invoice for rework:', error)
    throw new Error(error.response?.data?.message || 'Error sending invoice for rework')
  }
}

export const cancelInvoice = async (invoiceId: number, remark?: string): Promise<Invoice> => {
  try {
    const response = await axios.post<Invoice>(`${INVOICES_URL}/${invoiceId}/approval/cancel`, {
      remark,
    })
    return response.data
  } catch (error: any) {
    console.error('Error cancelling invoice:', error)
    throw new Error(error.response?.data?.message || 'Error cancelling invoice')
  }
}

// ——— DELIVERIES ————————————————————————————————————————————————————————————

export const getDeliveriesPage = async (
  page: number = 0,
  size: number = 10,
  filters?: {companyId?: number; companyType?: string; vesselId?: number}
): Promise<DeliveryPageResponse> => {
  try {
    console.log('Fetching deliveries from:', DELIVERIES_URL)
    const response = await axios.get<any>(DELIVERIES_URL, {
      params: {
        page,
        size,
        ...(filters?.companyId ? {companyId: filters.companyId} : {}),
        ...(filters?.companyType ? {companyType: filters.companyType} : {}),
        ...(filters?.vesselId ? {vesselId: filters.vesselId} : {}),
      },
      headers: {
        Accept: 'application/json',
      },
    })
    console.log('Deliveries fetched successfully:', response.data)
    return normalizePagedResponse<Delivery>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching deliveries:', error)
    throw new Error(error.response?.data?.message || 'Error fetching deliveries')
  }
}

export const getDeliveries = async (): Promise<Delivery[]> => {
  const page = await getDeliveriesPage(0, 1000)
  return page.content || []
}

export const getDeliveryById = async (deliveryId: number): Promise<Delivery> => {
  try {
    const response = await axios.get<Delivery>(`${DELIVERIES_URL}/${deliveryId}`, {
      headers: {
        Accept: 'application/json',
      },
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching delivery:', error)
    throw new Error(error.response?.data?.message || 'Error fetching delivery')
  }
}

export const uploadDeliveryProof = async (deliveryId: number, files: File[]): Promise<Delivery> => {
  try {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    const response = await axios.post<Delivery>(`${DELIVERIES_URL}/${deliveryId}/proof`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    })
    return response.data
  } catch (error: any) {
    console.error('Error uploading delivery proof:', error)
    throw new Error(error.response?.data?.message || 'Error uploading delivery proof')
  }
}

export const getProcurementLogs = async (params?: {
  moduleName?: string
  entityName?: string
  referenceNo?: string
  q?: string
  fromDate?: string
  toDate?: string
}): Promise<ProcurementAuditLog[]> => {
  try {
    const response = await axios.get<ProcurementAuditLog[]>(PROCUREMENT_LOGS_URL, {params})
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching procurement logs:', error)
    throw new Error(error.response?.data?.message || 'Error fetching procurement logs')
  }
}

export const getProcurementLogsByReference = async (
  referenceNo: string
): Promise<ProcurementAuditLog[]> => {
  try {
    const response = await axios.get<ProcurementAuditLog[]>(
      `${PROCUREMENT_LOGS_URL}/by-reference`,
      {
        params: {referenceNo},
      }
    )
    return response.data || []
  } catch (error: any) {
    console.error('Error fetching procurement logs by reference:', error)
    throw new Error(error.response?.data?.message || 'Error fetching procurement logs')
  }
}

export const getProcurementLogSummaries = async (params?: {
  moduleName?: string
  entityName?: string
  referenceNo?: string
  q?: string
  fromDate?: string
  toDate?: string
  page?: number
  size?: number
}): Promise<ProcurementAuditLogSummaryPageResponse> => {
  try {
    const page = params?.page ?? 0
    const size = params?.size ?? 25
    const response = await axios.get<ProcurementAuditLogSummaryPageResponse>(
      `${PROCUREMENT_LOGS_URL}/summary`,
      {
        params: {
          ...params,
          page,
          size,
        },
      }
    )
    return normalizePagedResponse<ProcurementAuditLogSummary>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching procurement log summaries:', error)
    throw new Error(error.response?.data?.message || 'Error fetching procurement log summaries')
  }
}

export const getProcurementLogsByReferences = async (
  referenceNos: string[],
  params?: {
    page?: number
    size?: number
  }
): Promise<ProcurementAuditLogSummaryPageResponse> => {
  try {
    const page = params?.page ?? 0
    const size = params?.size ?? 25
    const normalizedReferenceNos = Array.from(
      new Set(referenceNos.map((referenceNo) => String(referenceNo || '').trim()).filter(Boolean))
    )
    const response = await axios.get<ProcurementAuditLogSummaryPageResponse>(
      `${PROCUREMENT_LOGS_URL}/by-references`,
      {
        params: {
          referenceNos: normalizedReferenceNos,
          page,
          size,
        },
        paramsSerializer: (requestParams) => serializeProcurementQueryParams(requestParams),
      }
    )
    return normalizePagedResponse<ProcurementAuditLogSummary>(response.data, page, size)
  } catch (error: any) {
    console.error('Error fetching procurement logs by references:', error)
    throw new Error(error.response?.data?.message || 'Error fetching procurement logs')
  }
}

export const getProcurementLogById = async (
  id: number
): Promise<ProcurementAuditLogDetail> => {
  try {
    const response = await axios.get<ProcurementAuditLogDetail>(`${PROCUREMENT_LOGS_URL}/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching procurement log detail:', error)
    throw new Error(error.response?.data?.message || 'Error fetching procurement log detail')
  }
}

const VENDOR_LOGISTICS_CHANGE_REQUESTS_URL = `${API_URL}/procurement/vendor-logistics-change-requests`
const VENDOR_PORTAL_LOGISTICS_CHANGE_REQUESTS_URL = `${API_URL}/vendor/logistics-change-requests`

export interface VendorLogisticsChangeRequest {
  id: number
  quotationId?: number | null
  rfqId?: number | null
  rfqCode?: string | null
  rfqTitle?: string | null
  requisitionId?: number | null
  requisitionCode?: string | null
  purchaseOrderId?: number | null
  poNumber?: string | null
  vendorId?: number | null
  vendorName?: string | null
  currency?: string | null
  status?: string | null
  oldFreightCost?: number | null
  oldPackagingCost?: number | null
  oldTruckCost?: number | null
  oldBargeCost?: number | null
  oldOtherCost?: number | null
  oldVatOnLogistics?: number | null
  oldTotalDiscount?: number | null
  oldQuotationTotal?: number | null
  requestedFreightCost?: number | null
  requestedPackagingCost?: number | null
  requestedTruckCost?: number | null
  requestedBargeCost?: number | null
  requestedOtherCost?: number | null
  requestedVatOnLogistics?: number | null
  requestedTotalDiscount?: number | null
  requestedQuotationTotal?: number | null
  totalDifference?: number | null
  vendorRemarks?: string | null
  rejectionReason?: string | null
  requestedAt?: string | null
  requestedByUserId?: number | null
  requestedByUsername?: string | null
  reviewedAt?: string | null
  reviewedByUserId?: number | null
  reviewedByUsername?: string | null
}

export interface VendorLogisticsChangeRequestFilters {
  status?: string
  rfqId?: number
  purchaseOrderId?: number
  quotationId?: number
  vendorId?: number
}

export interface VendorLogisticsChangeContext {
  quotationId?: number | null
  purchaseOrderId?: number | null
  canSubmitLogisticsChangeRequest: boolean
  logisticsChangeRequestStatus?: string | null
  logisticsChangeRequestLockReason?: string | null
}

export const getVendorLogisticsChangeContextForQuotation = async (
  quotationId: number
): Promise<VendorLogisticsChangeContext> => {
  const response = await axios.get<VendorLogisticsChangeContext>(
    `${VENDOR_PORTAL_LOGISTICS_CHANGE_REQUESTS_URL}/quotation/${quotationId}/context`
  )
  return response.data
}

export const submitVendorLogisticsChangeRequest = async (body: {
  quotationId: number
  purchaseOrderId?: number | null
  freightCost?: number
  packagingCost?: number
  truckCost?: number
  bargeCost?: number
  otherCost?: number
  vatOnLogistics?: number
  totalDiscount?: number
  vendorRemarks?: string
}): Promise<VendorLogisticsChangeRequest> => {
  const response = await axios.post<VendorLogisticsChangeRequest>(
    VENDOR_PORTAL_LOGISTICS_CHANGE_REQUESTS_URL,
    body
  )
  return response.data
}

export const listVendorLogisticsChangeRequests = async (
  filters?: string | VendorLogisticsChangeRequestFilters
): Promise<VendorLogisticsChangeRequest[]> => {
  try {
    const params = typeof filters === 'string' ? {status: filters} : filters
    const response = await axios.get<VendorLogisticsChangeRequest[]>(
      VENDOR_LOGISTICS_CHANGE_REQUESTS_URL,
      {params}
    )
    return response.data || []
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Error fetching vendor logistics change requests'
    )
  }
}

export const getVendorLogisticsChangeRequest = async (
  requestId: number
): Promise<VendorLogisticsChangeRequest> => {
  try {
    const response = await axios.get<VendorLogisticsChangeRequest>(
      `${VENDOR_LOGISTICS_CHANGE_REQUESTS_URL}/${requestId}`
    )
    return response.data
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Error fetching vendor logistics change request'
    )
  }
}

export const approveVendorLogisticsChangeRequest = async (
  requestId: number,
  remark?: string
): Promise<VendorLogisticsChangeRequest> => {
  try {
    const response = await axios.post<VendorLogisticsChangeRequest>(
      `${VENDOR_LOGISTICS_CHANGE_REQUESTS_URL}/${requestId}/approve`,
      {remark}
    )
    return response.data
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Error approving vendor logistics change request'
    )
  }
}

export const rejectVendorLogisticsChangeRequest = async (
  requestId: number,
  reason: string
): Promise<VendorLogisticsChangeRequest> => {
  try {
    const response = await axios.post<VendorLogisticsChangeRequest>(
      `${VENDOR_LOGISTICS_CHANGE_REQUESTS_URL}/${requestId}/reject`,
      {reason}
    )
    return response.data
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Error rejecting vendor logistics change request'
    )
  }
}
